// /ad-strategist — competitive ad intelligence (open to all plans).
//
// User adds competitors (Facebook page URLs/handles); we pull their LIVE ads
// from the public Meta Ad Library, analyze them against the user's brand, and
// return a competitive brief + 5 concept directions engineered to beat them.
// Each concept has a "Generate this →" that drops the user into the matching
// wizard route with the concept stashed for reference.
//
// Open to all plans (2026-06-08 — premium gate removed). The run is
// synchronous + long (vendor pull + LLM), so we show staged progress
// while awaiting.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import AccordionStep, { type SectionState } from '@/pages/appv2/components/Section';
import { useCredits } from '@/hooks/useCredits';
import { useConfirm } from '@/hooks/useConfirm';
import { useBrand } from '@/hooks/useBrand';
import {
  getBrand,
  listProducts,
  listAdStrategistJobs,
  getAdStrategistJob,
  updateAdStrategistJob,
  deleteAdStrategistJob,
  markAllAdStrategistViewed,
  runAdStrategist,
  renderAdConcept,
  regenerateAdConcepts,
  queueAdStrategistRenders,
  buildAdStrategist20Concepts,
  addAdStrategist20More,
  renderAdStrategistConcept,
  getAdStrategistJobRenders,
  getAdStrategistRunPrecheck,
  type AdStrategistRunPrecheck,
  scrapeAdStrategistProduct,
  suggestCompetitors,
  listCompetitors,
  addCompetitor as addCompetitorToLibrary,
  type CompetitorSuggestion,
  type SavedCompetitor,
  type AdStrategistJob,
  type AdStrategistResult,
  type AdBrief,
  type AdConcept,
  type AdStrategist20Idea,
  type AdStrategist20Concept,
  type GatheredAd,
  type ProductCandidate,
  type UserProduct,
} from '@/lib/api';
import { buildTemplateCatalog, resolveTemplate } from '@/lib/templateCatalog';
import { savePost, listPosts, getSignedImageUrl, type Post } from '@/lib/gallery';
import ProductCatalogPicker, {
  type ProductPickerSelection,
} from '@/components/feature/ProductCatalogPicker';

const MAX_COMPETITORS = 5;

// Credit cost the user is charged when they click Generate. Must match
// AD_STRATEGIST_BATCH_CREDITS in backend/main.py. Used to surface the cost
// up front + gate the Generate button when balance is insufficient.
const GENERATE_COST = 5;

const ROUTE_LABELS: Record<string, string> = {
  'single': 'Single post',
  'carousel': 'Carousel',
  'product-shot': 'Product shot',
  'product-swap': 'Product swap',
};

// Competitor-aware run — wording mirrors what the worker is actually doing
// (Apify pulls → LLM brief that includes competitor_landscape + gap_analysis).
const PROGRESS_STAGES = [
  'Pulling competitor ads from the ad library…',
  'Reading hooks, formats, and what’s been running longest…',
  'Mapping the competitive landscape…',
  'Finding the gaps your competitors aren’t exploiting…',
  'Writing your brief + 5 concepts…',
];

// No-competitor run — the Apify scrape is skipped entirely (competitors[] is
// empty server-side) and the LLM brief leans on the brand identity + saved
// product catalog instead. Mention competitors here at all would be wrong.
const PROGRESS_STAGES_NO_COMPETITORS = [
  'Reading your brand identity + product catalog…',
  'Spotting where your brand can stand out…',
  'Drafting strategic angles built around your products…',
  'Polishing the brief…',
  'Writing your brief + 5 concepts…',
];

interface CompetitorRow {
  label: string;
  query: string;
}

export default function AdStrategistPage() {
  const navigate = useNavigate();

  // Tablet + desktop only — see the isMobile gate before the main return.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { activeBrand } = useBrand();
  const { refresh: refreshCredits } = useCredits();

  const [brandName, setBrandName] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [brandQuery, setBrandQuery] = useState('');
  const [brandWebsite, setBrandWebsite] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [competitors, setCompetitors] = useState<CompetitorRow[]>([
    { label: '', query: '' },
    { label: '', query: '' },
  ]);
  // Saved competitor library — the new canonical source. Loaded from
  // /api/competitors on mount. The form's submitted competitor list is
  // derived from `selectedSavedIds` (multi-select checklist of saved
  // competitors). The legacy `competitors` state above stays as the
  // submit payload — it's populated from selectedSavedIds in an effect.
  const [savedCompetitors, setSavedCompetitors] = useState<SavedCompetitor[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [selectedSavedIds, setSelectedSavedIds] = useState<Set<string>>(new Set());
  // Competitor SUGGESTIONS — LLM+Apify-validated picks the user can
  // add with one click instead of typing FB handles by hand. Lazy: only
  // fires when user clicks "Suggest". Cached 14d on the backend so a
  // repeat click for the same brand is instant.
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [suggestionsFromCache, setSuggestionsFromCache] = useState(false);
  // True when the backend pulled real product signals (My Products
  // categories + tags) into the LLM prompt. False → picks were brand-
  // description-only — surface a hint so the user knows adding products
  // improves accuracy.
  const [suggestionsUsedProducts, setSuggestionsUsedProducts] = useState(true);

  const [running, setRunning] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [result, setResult] = useState<AdStrategistResult | null>(null);
  // Buffered API response held during the stepper flow. handleRun writes
  // here (not directly to `result`) so the user can view the gathered ads
  // in Step 3 and the brief in Step 4 inside the stepper UI. Subsequent
  // steps (5 + 6) ALSO read/mutate this — handleConfirmProduct patches
  // brief.concepts in here when regenerateAdConcepts returns.
  const [liveResult, setLiveResult] = useState<AdStrategistResult | null>(null);
  const [liveJobId, setLiveJobId] = useState<string | null>(null);
  // Step 5 (pick a product) + Step 6 (generated ads) state. confirmedProduct
  // = what the user picked; briefProductFor = which product the current
  // concepts are written for (used to skip a redundant regenerateAdConcepts
  // call if they pick the same product again).
  const [confirmedProduct, setConfirmedProduct] = useState<ProductInfo | null>(null);
  const [briefProductFor, setBriefProductFor] = useState<string>('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  // Job id for the current results (used by regenerate-concepts so it can
  // reuse the already-gathered intel instead of re-scraping).
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Suggested product for the handoff — the user's most-recent saved product
  // (if any). The wizard lets them swap it. null when the library is empty.
  const [suggestedProductId, setSuggestedProductId] = useState<string | null>(null);
  const [suggestedProductName, setSuggestedProductName] = useState<string>('');
  const [suggestedProductDescription, setSuggestedProductDescription] = useState<string>('');
  // Full My Products list — passed to the post-brief Product Picker.
  const [savedProducts, setSavedProducts] = useState<UserProduct[]>([]);

  // Auto-rerun cadence (the run form's optional schedule field). null = off.
  // The "Name this analysis" input was removed — felt too crowded; falls back
  // to the client-side "{brand_name} · {date}" label in the recent list.
  const [rerunIntervalDays, setRerunIntervalDays] = useState<number | null>(null);

  // Recent runs shown above the form. Loaded on mount; refreshed after each
  // new run / rename. Holds light rows (no result blob).
  const [recentJobs, setRecentJobs] = useState<AdStrategistJob[]>([]);
  // False until the first listAdStrategistJobs() resolves. Prevents the form
  // from briefly flashing on the user's screen while we're still figuring
  // out whether they have past analyses (in which case the cards view is
  // the right landing). Without this, hasRecent starts false → showFormView
  // is true on the first paint → form renders → fetch returns → flips to
  // cards. The flash is short but jarring.
  const [recentJobsLoaded, setRecentJobsLoaded] = useState(false);

  // Saved posts grouped by source_job_id — drives the thumbnail strip on
  // the SavedAdsGrid shown when the user opens a LOCKED archived analysis
  // (past run that already had concepts when re-opened). Only posts saved
  // FROM Ad Strategist (i.e. with a non-null source_job_id) get bucketed;
  // the rest fall through. Loaded once on mount + refreshed after each
  // successful run so newly-saved ads appear without a page reload.
  const [postsByJob, setPostsByJob] = useState<Record<string, Post[]>>({});
  // True when the user opened a past analysis from the Recent list — the
  // result on screen is the snapshot of that day's brief. Generate still
  // works (uses regenerateAdConcepts which reads cached intel — no Apify
  // rerun) but the input form stays hidden.
  const [viewingArchived, setViewingArchived] = useState(false);
  // When there are recent analyses, the page lands on the cards view.
  // Clicking the top-right "Start new analysis" CTA flips this true and
  // reveals the form (replacing the cards). When there are NO recent jobs
  // yet, the form is the default landing view (no CTA, no Back).
  const [showingForm, setShowingForm] = useState(false);

  // Surfaced when the user tries to start an analysis with zero saved
  // competitors. The analysis CAN run without them (the brief is built from
  // the brand identity + product candidates + optional FB-handle ads) but
  // the result is sharper with competitor context, so we give the user a
  // chance to add some first instead of just barrelling ahead.
  const [showNoCompetitorsModal, setShowNoCompetitorsModal] = useState(false);

  // Run-precheck state. Populated whenever the user clicks "Start new
  // analysis" — drives the recency warning modal (when the next run will
  // cost credits AND the last run was <7 days ago) and the credit cost
  // copy on the Run CTA. Null until the first fetch resolves.
  const [runPrecheck, setRunPrecheck] = useState<AdStrategistRunPrecheck | null>(null);
  // Visible when (is_first_run=false AND days_since_last<7) — user opted
  // into a rerun before the typical competitor-ad refresh window closed.
  // Modal explains they'll pay credits + the brief will use a freshness
  // nudge to push past first-pass angles.
  const [showRecencyModal, setShowRecencyModal] = useState(false);

  // "+ 20 more from this strategy" CTA on the archived view. Single in-
  // flight flag because the user can only run one at a time; error sticks
  // until the next attempt clears it.
  const [add20MoreBusy, setAdd20MoreBusy] = useState(false);
  const [add20MoreError, setAdd20MoreError] = useState<string | null>(null);

  // ---- Phase 2 (multi-product 20-concepts) state ----
  // Picker selection bubbles up from <ProductCatalogPicker> in Step 4.
  // Initial state: All Products toggle ON (default behaviour per UX lock).
  // When the user clicks Build winning ideas, we POST to
  // /api/ad-strategist/build-20-concepts with this selection.
  const [phase2Selection, setPhase2Selection] = useState<ProductPickerSelection>({
    selectedIds: [],
    collectionId: null,
    allSelected: true,
  });
  /** Phase 2 LLM output: 5 ideas × 4 distinct concepts. Populated after
   *  the build-20-concepts call returns. Null while not yet fetched, or
   *  cleared on re-entry to Step 4 (so the user gets a fresh call). */
  const [phase2Ideas, setPhase2Ideas] = useState<AdStrategist20Idea[] | null>(null);
  const [phase2Building, setPhase2Building] = useState(false);
  const [phase2Error, setPhase2Error] = useState<string | null>(null);

  // ---- Per-concept render state (NEW model — replaces stub-locked tiles) ----
  // Each Winning Ideas card has its own Generate button. The status map is
  // keyed by "ideaIdx_conceptIdx" → {status, postId, imageUrl}. Hydrated
  // from /jobs/{id}/renders on mount; polled every 4s while any are 'queued'.
  // Concepts not in the map are 'idle' (Generate button visible).
  type ConceptRenderState = {
    status: 'queued' | 'rendered';
    postId: string;
    imageUrl: string | null;
  };
  const [conceptRenderMap, setConceptRenderMap] = useState<Map<string, ConceptRenderState>>(
    new Map(),
  );
  // Optimistic local "in flight" set — populated the moment the user clicks
  // Generate, before the backend acks. Cleared once the backend's response
  // lands (status moves to 'queued' in conceptRenderMap, which supersedes
  // this). Prevents double-clicks from firing two renders for the same slot.
  const [pendingGenerateKeys, setPendingGenerateKeys] = useState<Set<string>>(new Set());
  // Generate-all flow.
  const [generateAllBusy, setGenerateAllBusy] = useState(false);
  const [showGenerateAllConfirm, setShowGenerateAllConfirm] = useState(false);
  const [generateAllError, setGenerateAllError] = useState<string | null>(null);

  // Accordion stepper state (1..6) for the run form. Mirrors /appv2's
  // wizard pattern — only one section is "active" at a time; previous
  // sections collapse to a summary row with an Edit pill.
  //   1: Your brand information
  //   2: Your competitors
  //   3: Competitor ads + Phase 1 brief (auto-fires the API on entry)
  //   4: Product selection (NEW — ProductCatalogPicker, All Products by default)
  //   5: Winning Ideas — 5 buckets × 4 concepts, Build my ads CTA
  //   6: (loading-then-redirect) — kept in the type union for back-compat;
  //      Step 5's CTA fires queue-renders + navigates to /gallery.
  // Past Step 6 the user navigates away via "Back to analyses" — there's
  // no Step 7. ResultsView is reserved for ARCHIVED opens only.
  type FormStep = 1 | 2 | 3 | 4 | 5 | 6;
  const [formStep, setFormStep] = useState<FormStep>(1);
  // Step state lookup — locked / active / completed per section index.
  // Sections that haven't been reached yet are locked; the current one is
  // active; everything before it is completed (collapsed to summary).
  const stepState = (n: FormStep): SectionState =>
    formStep === n ? 'active' : formStep > n ? 'completed' : 'locked';
  // Resetting the form (e.g. when the user backs out via "Back to analyses")
  // returns to Step 1. Keeps brand-name pre-fills intact since those are
  // expensive to re-type.
  const resetFormStep = useCallback(() => {
    setFormStep(1);
    setError(null);
  }, []);

  // Last values we prefilled from My Brand. A field is "unedited" while it
  // still equals this — so a refetch can refresh it without clobbering
  // anything the user typed.
  const prefilledRef = useRef({ name: '', description: '', website: '' });

  // Fetch the latest brand and apply it: the logo (no input field) always
  // updates; the editable fields update only if still unedited since the last
  // prefill. Called on mount AND on window focus so a brand edited elsewhere
  // (or in another tab) is reflected without re-typing.
  const loadBrand = useCallback(async () => {
    try {
      const brand = await getBrand();
      if (!brand) return;
      setBrandLogoUrl(brand.logoUrl || '');
      const prev = prefilledRef.current;
      const next = {
        name: brand.name || '',
        description: brand.brief || '',
        website: brand.sourceUrl || '',
      };
      setBrandName((cur) => (cur === prev.name || cur === '' ? next.name : cur));
      setBrandDescription((cur) => (cur === prev.description || cur === '' ? next.description : cur));
      setBrandWebsite((cur) => (cur === prev.website || cur === '' ? next.website : cur));
      prefilledRef.current = next;
    } catch {
      /* no brand yet — fields stay as-is */
    }
  }, []);

  // Re-fetch the brand whenever the page/tab regains focus.
  useEffect(() => {
    const onFocus = () => { loadBrand(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadBrand]);

  // On mount: load brand + a default product + prefill competitors.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadBrand();
      try {
        const products = await listProducts();
        if (!cancelled) {
          setSavedProducts(products);
          if (products.length > 0) {
            setSuggestedProductId(products[0].id);
            setSuggestedProductName(products[0].name || '');
            setSuggestedProductDescription(products[0].description || '');
          }
        }
      } catch {
        /* no products — handoff just won't pre-pick one */
      }
      // Pre-fill the competitor list (+ brand FB page) from the most recent
      // run so the user doesn't re-enter them every time. The run input is
      // persisted in ad_strategist_jobs — no extra storage needed. Also feeds
      // the "Recent analyses" list rendered above the form.
      try {
        const jobs = await listAdStrategistJobs();
        if (cancelled) return;
        setRecentJobs(jobs);
        setRecentJobsLoaded(true);
        const input = jobs[0]?.input as
          | { brand_query?: string; competitors?: { label?: string; query?: string }[] }
          | undefined;
        if (input?.competitors && input.competitors.length > 0) {
          setCompetitors(
            input.competitors.map((c) => ({ label: c.label || '', query: c.query || '' })),
          );
          if (input.brand_query) setBrandQuery(input.brand_query);
        }
        // Visiting the page clears the sidebar's unread badge — fire-and-forget.
        void markAllAdStrategistViewed();
      } catch {
        // Endpoint down / not authed — flip "loaded" so the form lands
        // (empty recentJobs counts as "no past analyses", which is the
        // correct fallback UX).
        if (!cancelled) setRecentJobsLoaded(true);
      }
      // Pull the active brand's saved posts and bucket by source_job_id so
      // each SavedAdsGrid can show them when the user opens a LOCKED analysis.
      // Skip rows without a source_job_id (those are wizard saves, unrelated).
      try {
        const all = await listPosts(activeBrand?.id ?? null);
        if (cancelled) return;
        const map: Record<string, Post[]> = {};
        for (const p of all) {
          if (!p.source_job_id) continue;
          (map[p.source_job_id] ||= []).push(p);
        }
        setPostsByJob(map);
      } catch {
        /* no posts / not authed — cards just render without thumbnails */
      }
    })();
    return () => { cancelled = true; };
  }, [loadBrand, activeBrand?.id]);

  // Cycle the staged progress labels while the (synchronous) run is in flight.
  const stageTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (running) {
      setStageIdx(0);
      stageTimer.current = setInterval(() => {
        setStageIdx((i) => Math.min(i + 1, PROGRESS_STAGES.length - 1));
      }, 9000);
    } else if (stageTimer.current) {
      clearInterval(stageTimer.current);
      stageTimer.current = null;
    }
    return () => {
      if (stageTimer.current) clearInterval(stageTimer.current);
    };
  }, [running]);

  const validCompetitors = competitors.filter((c) => c.query.trim().length > 0);
  // Drives the "running without competitors" flow: when true, the wizard
  // hides Step 3 (Competitor ads) entirely + Step 4 takes over the active
  // slot with "Winning Ideas" copy. After the run completes we also
  // auto-advance formStep so the user lands on Step 4 without a click.
  // True both for in-flight runs (use validCompetitors) and re-opened
  // results (use liveResult). archived re-opens go through a different
  // path (ResultsView) so this only matters for the streamlined flow.
  const hasCompetitors =
    validCompetitors.length > 0
    || (liveResult?.gathered.competitors.length ?? 0) > 0;
  // Loading copy follows the same branch — Apify-mentioning stages are
  // wrong when the run skips Apify entirely. Both arrays are 5 entries
  // long so the existing stageIdx cycler keeps working unchanged.
  const progressStages = hasCompetitors
    ? PROGRESS_STAGES
    : PROGRESS_STAGES_NO_COMPETITORS;
  // `canRun` was used by the old single-shot "Analyze the competition"
  // button. The stepper now drives validation per step (Step 1 needs
  // brandName, Step 2 needs ≥1 competitor) so the legacy gate is gone.

  const setCompetitorAt = (idx: number, patch: Partial<CompetitorRow>) => {
    setCompetitors((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addCompetitor = () =>
    setCompetitors((rows) => (rows.length < MAX_COMPETITORS ? [...rows, { label: '', query: '' }] : rows));
  const removeCompetitor = (idx: number) =>
    setCompetitors((rows) => rows.filter((_, i) => i !== idx));

  // Suggest-competitors flow — pulls LLM picks + Apify-validates each.
  // Slow first run (30-60s parallel Apify), instant on cache hit.
  const handleSuggestCompetitors = useCallback(async () => {
    setSuggestionsError(null);
    setSuggestionsLoading(true);
    try {
      const result = await suggestCompetitors();
      setSuggestions(result.suggestions);
      setSuggestionsFromCache(result.from_cache);
      setSuggestionsUsedProducts(result.product_signals_used);
    } catch (err) {
      setSuggestionsError(
        err instanceof Error ? err.message : 'Could not get suggestions. Try again.',
      );
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  // Load saved competitors on mount + on brand switch. The backend already
  // scopes to the active brand server-side; re-firing here ensures the
  // pre-selected set matches the freshly-loaded list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSavedLoading(true);
      try {
        const result = await listCompetitors();
        if (!cancelled) {
          setSavedCompetitors(result.competitors);
          // Default: pre-select all saved competitors so a returning user
          // can just hit "Get competitor ads" without micromanaging.
          setSelectedSavedIds(new Set(result.competitors.map((c) => c.id)));
        }
      } catch (err) {
        console.error('[ad-strategist] load saved competitors failed', err);
      } finally {
        if (!cancelled) setSavedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // Derive the legacy `competitors` list (the AS run payload) from the
  // saved-competitor selection. Keeps the existing run/submit code path
  // unchanged while sourcing input from the saved library.
  useEffect(() => {
    const picked = savedCompetitors.filter((c) => selectedSavedIds.has(c.id));
    setCompetitors(
      picked.map((c) => ({ label: c.label, query: c.query })),
    );
  }, [savedCompetitors, selectedSavedIds]);

  // Shared entry point for both the empty-state CTA and the top-right
  // "New Analysis" button. Order of gates:
  //   1. Active brand required
  //   2. Run precheck — fetches credit cost + recency. First run is free;
  //      subsequent runs charge credits. If the last run was <7 days ago,
  //      surfaces the recency warning modal so the user opts in knowingly.
  //   3. Competitor presence — modal if none saved
  //   4. Otherwise → advance to Step 3 (Apify scrape auto-fires)
  const startAnalysis = async () => {
    setError(null);
    if (!activeBrand) {
      setError('Set up a brand first from My Brand.');
      return;
    }
    // Fetch precheck before deciding which path to take. Cheap call — caches
    // into runPrecheck so the wizard's run CTA can show the same cost.
    let pre = runPrecheck;
    try {
      pre = await getAdStrategistRunPrecheck();
      setRunPrecheck(pre);
    } catch (err) {
      // Non-fatal — fall through and treat as a normal run. The backend
      // will reject if credits are really insufficient.
      console.error('[ad-strategist] precheck failed:', err);
    }
    // Recency warning — only on RERUNS within the typical refresh window.
    // First runs and runs older than 7 days proceed straight through.
    if (pre && !pre.is_first_run && (pre.days_since_last ?? Infinity) < 7) {
      setShowRecencyModal(true);
      return;
    }
    proceedToRun();
  };

  // Actually transitions the wizard into the run flow. Used by both the
  // "no precheck warning needed" happy path AND the recency-modal "Run
  // anyway" button. Centralised so the competitor-presence check is
  // applied uniformly.
  const proceedToRun = () => {
    setShowRecencyModal(false);
    if (savedCompetitors.length === 0) {
      setShowNoCompetitorsModal(true);
      return;
    }
    setShowingForm(true);
    setFormStep(3);
  };

  // Modal "Run without competitors" path. Skips the saved-competitors check
  // and jumps straight into the wizard. handleRun (auto-fires on formStep=3)
  // sends an empty competitors[] which the backend now accepts.
  const runWithoutCompetitors = () => {
    setShowNoCompetitorsModal(false);
    setError(null);
    setShowingForm(true);
    setFormStep(3);
  };

  const toggleSavedCompetitor = (id: string) => {
    setSelectedSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Add a suggestion to the saved competitor library AND auto-select it.
  // Was previously: slot into the inline form's competitor input list.
  const handleAddSuggestion = async (sug: CompetitorSuggestion) => {
    // Skip dupes — if a saved competitor with this query exists, just select it.
    const existing = savedCompetitors.find((c) => c.query === sug.query);
    if (existing) {
      setSelectedSavedIds((prev) => new Set(prev).add(existing.id));
      return;
    }
    try {
      const result = await addCompetitorToLibrary({ query: sug.query, label: sug.name });
      setSavedCompetitors((prev) => [result.competitor, ...prev]);
      setSelectedSavedIds((prev) => new Set(prev).add(result.competitor.id));
    } catch (err) {
      console.error('[ad-strategist] add suggestion failed', err);
    }
  };

  // Legacy (kept for back-compat with the suggest panel below; no longer
  // called from anywhere else after the refactor).
  const _legacyAddSuggestionInline = (sug: CompetitorSuggestion) => {
    setCompetitors((rows) => {
      // Skip dupes — if the query is already in the list, no-op.
      if (rows.some((r) => r.query.trim() === sug.query)) return rows;
      const emptyIdx = rows.findIndex((r) => !r.query.trim());
      if (emptyIdx >= 0) {
        return rows.map((r, i) =>
          i === emptyIdx ? { label: sug.name, query: sug.query } : r,
        );
      }
      if (rows.length < MAX_COMPETITORS) {
        return [...rows, { label: sug.name, query: sug.query }];
      }
      return rows;
    });
  };

  const handleRun = useCallback(async () => {
    setError(null);
    setLiveResult(null);
    setLiveJobId(null);
    setViewingArchived(false);
    setRunning(true);
    try {
      const job = await runAdStrategist({
        brandName,
        brandDescription,
        brandQuery,
        competitors: validCompetitors.map((c) => ({ label: c.label || undefined, query: c.query.trim() })),
        templateCatalog: buildTemplateCatalog(),
        productId: suggestedProductId || undefined,
        productName: suggestedProductName || undefined,
        productDescription: suggestedProductDescription || undefined,
        brandWebsite: brandWebsite || undefined,
        rerunIntervalDays: rerunIntervalDays,
      });
      if (job.status === 'ready' && job.result) {
        // Buffer into liveResult — Step 3 + Step 4 render from this. The
        // user moves into "result mode" (ResultsView with picker) only
        // after Step 4 Continue.
        setLiveJobId(job.id);
        setLiveResult(job.result);
        // When the user opted to run without competitors, Step 3 (Competitor
        // ads) is hidden. Auto-advance to Step 4 so the user lands on the
        // Winning Ideas section instead of staring at a phantom "active"
        // step they can't see. Skipped when there ARE competitors — the
        // user clicks "See the openings" to advance manually.
        if (job.result.gathered.competitors.length === 0) {
          setFormStep(4);
        }
        // Refresh the recent list + saved-posts map so the new row + any
        // saved ads reflect immediately when the user backs out.
        try {
          const refreshed = await listAdStrategistJobs();
          setRecentJobs(refreshed);
        } catch { /* non-critical */ }
        try {
          const all = await listPosts(activeBrand?.id ?? null);
          const map: Record<string, Post[]> = {};
          for (const p of all) {
            if (!p.source_job_id) continue;
            (map[p.source_job_id] ||= []).push(p);
          }
          setPostsByJob(map);
        } catch { /* non-critical */ }
      } else {
        setError(job.error || 'The analysis did not complete. Please try again.');
        // Step back to Step 2 so the user can fix + retry without losing the
        // entered competitors/brand.
        setFormStep(2);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      setError(msg);
      // Step back to Step 2 so the user can fix + retry.
      setFormStep(2);
    } finally {
      setRunning(false);
    }
  }, [brandName, brandDescription, brandQuery, brandWebsite, validCompetitors, suggestedProductId, suggestedProductName, suggestedProductDescription, rerunIntervalDays]);

  // When the user clicks Continue on Step 2, formStep flips to 3 — that's
  // when we actually fire the analysis. Guarded against double-firing: only
  // fire when not already running AND we don't already have a live result
  // (which would mean the user navigated back/forward through the stepper
  // without re-running). The user can hit "Edit" on Step 2 to change inputs
  // + run again — `setLiveResult(null)` is the signal that they want a fresh
  // call. Today the Edit flow on Step 2 doesn't clear liveResult, so this
  // effect skips a redundant call when re-entering Step 3.
  useEffect(() => {
    if (formStep === 3 && !running && !liveResult) {
      void handleRun();
    }
  }, [formStep, running, liveResult, handleRun]);

  // Open a past analysis: load the cached job + restore its brief on screen
  // as a read-only snapshot. The user can still pick a product + Generate;
  // regenerateAdConcepts will reuse the snapshot's gathered intel (no Apify
  // rerun) so the only spend is the 5-credit concept-generation charge.
  const handleOpenJob = useCallback(async (id: string) => {
    setError(null);
    setRunning(true);
    try {
      const job = await getAdStrategistJob(id);
      if (job.status === 'ready' && job.result) {
        setJobId(job.id);
        setResult(job.result);
        setViewingArchived(true);
      } else {
        setError(job.error || 'That analysis is incomplete or failed.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open that analysis.');
    } finally {
      setRunning(false);
    }
  }, []);

  // Deep-link support — opens a specific AS job when ?job=<uuid> is in the
  // URL. Used by the calendar's "View Ad Strategist run" link on Ad Pilot
  // tiles. Runs once on mount; the URL param is cleared after open so a
  // refresh doesn't re-open (user can click again from the calendar).
  const didDeepLinkRef = useRef(false);
  useEffect(() => {
    if (didDeepLinkRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get('job');
    if (!target) return;
    didDeepLinkRef.current = true;
    void handleOpenJob(target);
    // Clear the ?job= param so it doesn't fire on subsequent renders.
    const url = new URL(window.location.href);
    url.searchParams.delete('job');
    window.history.replaceState({}, '', url.toString());
  }, [handleOpenJob]);

  const handleRenameJob = useCallback(async (id: string, name: string) => {
    try {
      const updated = await updateAdStrategistJob(id, { name });
      setRecentJobs((rows) => rows.map((r) => (r.id === id ? { ...r, name: updated.name } : r)));
    } catch { /* non-critical — leave the local label, the next fetch will recover */ }
  }, []);

  // Delete a saved analysis. Optimistic: drop from recentJobs immediately,
  // then fire the API. On failure, refetch the canonical list so the row
  // pops back. Posts saved from this analysis stay in My Creatives (FK ON
  // DELETE SET NULL); they just lose the source-job link.
  const handleDeleteJob = useCallback(async (id: string) => {
    setRecentJobs((rows) => rows.filter((r) => r.id !== id));
    // Also drop any cached saved-posts bucket for this job so SavedAdsGrid
    // (if rendered) doesn't try to surface them anymore.
    setPostsByJob((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      await deleteAdStrategistJob(id);
    } catch (err) {
      // Restore from server on failure.
      console.error('[ad-strategist] delete failed', err);
      try {
        const refreshed = await listAdStrategistJobs();
        setRecentJobs(refreshed);
      } catch { /* nothing more we can do */ }
    }
  }, []);

  // Per-concept product selection (concept index → product id). Default:
  // each concept inherits the most-recent saved product. User can override
  // any cell from the Step 5 picker grid.
  const [conceptProducts, setConceptProducts] = useState<Record<number, string>>({});
  const [queuing, setQueuing] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);

  // Auto-default per-concept picks once concepts arrive: every cell
  // points at the most-recent saved product. User can change any cell
  // via the dropdown; this just gives sensible defaults so Generate
  // works in one click even on a first-time visit.
  useEffect(() => {
    const concepts = liveResult?.brief?.concepts;
    if (!concepts || concepts.length === 0) return;
    if (savedProducts.length === 0) return;
    const defaultId = savedProducts[0].id;
    setConceptProducts((prev) => {
      // Don't overwrite anything the user explicitly picked.
      const next = { ...prev };
      for (let i = 0; i < concepts.length; i++) {
        if (next[i] === undefined) next[i] = defaultId;
      }
      return next;
    });
  }, [liveResult?.brief?.concepts, savedProducts]);

  // Streamlined Generate — fire queue-renders for all 5 (concept, product)
  // pairs, then navigate to /gallery so the user watches the 5 stubs
  // fill in. No inline AS rendering anymore.
  const handleQueueRenders = useCallback(async () => {
    if (!liveJobId) return;
    const concepts = liveResult?.brief?.concepts || [];
    if (concepts.length === 0) return;
    if (savedProducts.length === 0) {
      setQueueError('Add a product in My Products first.');
      return;
    }
    setQueueError(null);
    setQueuing(true);
    try {
      const fallbackProductId = savedProducts[0].id;
      const items = concepts.map((_c, idx) => ({
        conceptIdx: idx,
        productId: conceptProducts[idx] || fallbackProductId,
      }));
      const res = await queueAdStrategistRenders({
        jobId: liveJobId,
        items,
        aspectRatio: '1:1',
      });
      if (res.queued === 0) {
        setQueueError('Could not queue any renders. Check your products and try again.');
        setQueuing(false);
        return;
      }
      // Signal the gallery to poll for these specific post ids until
      // they unlock. Same sessionStorage pattern brand-setup uses; the
      // gallery's existing poll loop watches both keys.
      try {
        sessionStorage.setItem(
          'ad_strategist_active_renders',
          JSON.stringify({
            postIds: res.postIds,
            startedAt: Date.now(),
            count: res.queued,
          }),
        );
      } catch { /* sessionStorage quota — non-fatal */ }
      navigate('/gallery');
    } catch (err) {
      setQueueError(err instanceof Error ? err.message : 'Could not queue renders.');
      setQueuing(false);
    }
  }, [liveJobId, liveResult, conceptProducts, savedProducts, navigate]);

  // Step 4 → Step 5 transition. Pure advance (no LLM call). The user
  // moves from reading the Phase 1 strategic angles to the Step 5 product
  // picker, where they pick which products feed Phase 2.
  const handleStep4Continue = useCallback(() => {
    setFormStep(5);
  }, []);

  // Step 5 → Step 6 transition. Fires the Phase 2 build-20-concepts call
  // with the user's product selection from the picker. Backend produces
  // 5 ideas × 4 concepts and persists into the job's brief; we keep a
  // local copy in phase2Ideas for the Step 6 grid rendering.
  //
  // When the user lands on Step 6 with a stale phase2Ideas (e.g. they
  // re-entered Step 5 + edited the product selection), we re-fire to get
  // a fresh set. Idempotent — same selection produces the same call.
  const handleStep5Continue = useCallback(async () => {
    if (!liveJobId) {
      setPhase2Error('Run the analysis first.');
      return;
    }
    setPhase2Error(null);
    setPhase2Building(true);
    setFormStep(6); // advance eagerly so the user sees the loading state
    try {
      const result = await buildAdStrategist20Concepts({
        jobId: liveJobId,
        // null/empty = backend uses the full catalog (All Products toggle).
        productIds: phase2Selection.allSelected
          ? undefined
          : phase2Selection.selectedIds,
      });
      setPhase2Ideas(result.ideas);
    } catch (err) {
      setPhase2Error(err instanceof Error ? err.message : 'Could not build winning ideas.');
      setFormStep(5); // back to picker so the user can fix + retry
    } finally {
      setPhase2Building(false);
    }
  }, [liveJobId, phase2Selection]);

  // Step 6 CTA → "Build my ads". Fires queue-renders-20 which inserts all
  // 20 stubs into the gallery (5 unlocked + actively rendering, 15 locked
  // for per-1-credit unlock later). Hands off to /gallery via
  // sessionStorage so the gallery can show a "this batch is rendering…"
  // banner + auto-poll the 5 immediate renders until they flip.
  // handleBuildMyAds was the old "fire 20 renders all at once via the
  // queue-renders-20 endpoint then navigate to /gallery" flow. The new
  // model (per the May refactor) does on-demand renders via Generate-per-
  // card + Generate-all, both of which fire render-concept N times. The
  // user stays on this page and watches the cards flip live. handleBuildMyAds
  // is no longer called from any UI; kept only for the sessionStorage
  // poll-handoff in case future paths need to revive the bulk flow.

  // "+ 20 more from this strategy" CTA. Re-fires Phase 2 with the same
  // angles + products but instructs the LLM to diverge from every prior
  // batch.
  //
  // NEW MODEL: returns just the new ideas (no auto-render). We swap
  // brief.ideas in place + reset the per-card render map so the user
  // can Generate per-card or Generate-all on the fresh concepts.
  // Previously-rendered cards from the prior batch are still in My
  // Creatives — they just no longer appear in the WinningIdeasGrid.
  const handleAdd20More = useCallback(async (targetJobId: string) => {
    setAdd20MoreError(null);
    setAdd20MoreBusy(true);
    try {
      const r = await addAdStrategist20More({ jobId: targetJobId });
      // Update the in-memory result (archived view) OR the live result
      // (live wizard) — whichever surface is showing. Both store the
      // brief.ideas at the same path.
      setResult((prev) =>
        prev ? { ...prev, brief: { ...prev.brief, ideas: r.ideas } } : prev,
      );
      setLiveResult((prev) =>
        prev ? { ...prev, brief: { ...prev.brief, ideas: r.ideas } } : prev,
      );
      setPhase2Ideas(r.ideas);
      // Reset per-card map — none of the new slots have been rendered yet.
      // Polling effect will re-hydrate from /jobs/{id}/renders on its
      // next tick, which for the new ideas[] will be empty.
      setConceptRenderMap(new Map());
      setPendingGenerateKeys(new Set());
    } catch (err) {
      setAdd20MoreError(err instanceof Error ? err.message : 'Could not add more ads.');
    } finally {
      setAdd20MoreBusy(false);
    }
  }, []);

  // ── Per-concept render management ─────────────────────────────────────
  // The Winning Ideas grid now has a Generate button on each card. State
  // flow: idle (no entry in conceptRenderMap) → queued (after click,
  // post inserted, worker spawned) → rendered (image_path filled). The
  // map is hydrated from /jobs/{id}/renders on mount + polled while any
  // are queued.

  // Determine which job_id the WinningIdeasGrid is currently rendering
  // for. Live wizard flow uses liveJobId; archived flow uses jobId (from
  // ResultsView). Wrapped in a memo so the effect doesn't re-fire on every
  // unrelated state change.
  const winningIdeasJobId = liveJobId || jobId || null;

  /** Single concept key used everywhere — keeps Map ops type-safe. */
  const conceptKey = (ii: number, ci: number) => `${ii}_${ci}`;

  // Orphaned render count — rendered posts attached to this job that lost
  // their (idea_idx, concept_idx) slot link (pre-fix worker code NULLed
  // render_config). Shown as a "+ N from this analysis already in My
  // Creatives" pill so the user knows their credits weren't wasted.
  const [orphanedRenderCount, setOrphanedRenderCount] = useState(0);

  // Hydrate the conceptRenderMap from the backend whenever the grid's
  // job_id changes. Runs once on mount per job; the polling effect below
  // takes over from here. Failure is non-fatal — leaves the map empty so
  // every card shows the Generate button.
  useEffect(() => {
    if (!winningIdeasJobId) {
      setConceptRenderMap(new Map());
      setPendingGenerateKeys(new Set());
      setOrphanedRenderCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getAdStrategistJobRenders(winningIdeasJobId);
        if (cancelled) return;
        const next = new Map<string, ConceptRenderState>();
        for (const r of resp.renders) {
          if (r.idea_idx < 0 || r.concept_idx < 0) continue;
          next.set(conceptKey(r.idea_idx, r.concept_idx), {
            status: r.status,
            postId: r.post_id,
            imageUrl: r.image_url,
          });
        }
        setConceptRenderMap(next);
        setOrphanedRenderCount(resp.orphaned_count);
      } catch {
        /* keep empty map — degrades to all-idle, user can still generate */
      }
    })();
    return () => { cancelled = true; };
  }, [winningIdeasJobId]);

  // Poll while any concept is queued. Stops automatically once everything
  // has flipped to 'rendered' (or back to empty if all renders failed).
  // Same 4s cadence as the brand-setup polling on /gallery.
  useEffect(() => {
    if (!winningIdeasJobId) return;
    const anyQueued = Array.from(conceptRenderMap.values()).some((v) => v.status === 'queued');
    if (!anyQueued && pendingGenerateKeys.size === 0) return;
    const t = setInterval(async () => {
      try {
        const resp = await getAdStrategistJobRenders(winningIdeasJobId);
        const next = new Map<string, ConceptRenderState>();
        for (const r of resp.renders) {
          if (r.idea_idx < 0 || r.concept_idx < 0) continue;
          next.set(conceptKey(r.idea_idx, r.concept_idx), {
            status: r.status,
            postId: r.post_id,
            imageUrl: r.image_url,
          });
        }
        // Merge with existing map — don't drop entries that backend may
        // not have returned yet (eg. just-inserted post not visible to
        // the SELECT due to transaction visibility). For each key in the
        // current map, keep it if backend didn't override.
        setConceptRenderMap((prev) => {
          const merged = new Map(prev);
          // First, prefer backend's authoritative values for any key it returned.
          for (const [k, v] of next) merged.set(k, v);
          // Then, prune any LOCAL keys that backend KNOWS about but
          // ISN'T returning (eg. user deleted the post manually) — but
          // ONLY if backend's response has any renders at all. Empty
          // response could mean transient fetch failure; don't wipe.
          return merged;
        });
        setOrphanedRenderCount(resp.orphaned_count);
        // Clear pending keys that the backend has now confirmed.
        setPendingGenerateKeys((prev) => {
          const out = new Set(prev);
          for (const k of next.keys()) out.delete(k);
          return out;
        });
      } catch { /* swallow; try again next tick */ }
    }, 4000);
    return () => clearInterval(t);
  }, [winningIdeasJobId, conceptRenderMap, pendingGenerateKeys]);

  // Per-card Generate — fires the render-concept endpoint and optimistically
  // marks the key as pending so the card's button disables and a spinner
  // shows. Backend ack populates conceptRenderMap with 'queued', then the
  // poll picks up the 'rendered' transition.
  const handleGenerateConceptForCard = useCallback(async (ideaIdx: number, conceptIdx: number) => {
    if (!winningIdeasJobId) return;
    const key = conceptKey(ideaIdx, conceptIdx);
    // Skip if already in flight or rendered.
    if (pendingGenerateKeys.has(key)) return;
    const existing = conceptRenderMap.get(key);
    if (existing) return;
    setPendingGenerateKeys((prev) => {
      const out = new Set(prev);
      out.add(key);
      return out;
    });
    try {
      const r = await renderAdStrategistConcept({
        jobId: winningIdeasJobId,
        ideaIdx,
        conceptIdx,
        aspectRatio: '1:1',
      });
      // Merge into conceptRenderMap directly — don't wait for the poll.
      setConceptRenderMap((prev) => {
        const out = new Map(prev);
        out.set(key, {
          status: r.status,
          postId: r.post_id,
          imageUrl: null,
        });
        return out;
      });
      // Refresh credit balance — render-concept reserves 1 credit.
      refreshCredits();
    } catch (err) {
      // Surface the error globally; clearing the pending key lets the user
      // retry. Insufficient credits hits this path.
      setGenerateAllError(
        err instanceof Error ? err.message : 'Could not queue render.',
      );
    } finally {
      setPendingGenerateKeys((prev) => {
        const out = new Set(prev);
        out.delete(key);
        return out;
      });
    }
  }, [winningIdeasJobId, pendingGenerateKeys, conceptRenderMap, refreshCredits]);

  // Count of concepts that haven't been generated yet for the Generate-all
  // button label + confirm dialog. Used by both the wizard Step 6 button
  // and the archived ResultsView "Generate remaining" CTA.
  const idleConceptCount = (() => {
    const ideas = phase2Ideas || result?.brief.ideas || [];
    let n = 0;
    for (let ii = 0; ii < ideas.length; ii++) {
      const concepts = ideas[ii]?.concepts || [];
      for (let ci = 0; ci < concepts.length; ci++) {
        const k = conceptKey(ii, ci);
        if (!conceptRenderMap.has(k) && !pendingGenerateKeys.has(k)) n++;
      }
    }
    return n;
  })();

  // Generate-all — fires render-concept in parallel for every concept that
  // isn't already rendered, queued, or pending. Locks the per-card buttons
  // (via generateAllBusy + pendingGenerateKeys flooding) while in flight so
  // the user can't trigger duplicates mid-batch.
  const handleGenerateAll = useCallback(async () => {
    if (!winningIdeasJobId) return;
    const ideas = phase2Ideas || result?.brief.ideas || [];
    if (ideas.length === 0) return;
    setGenerateAllBusy(true);
    setGenerateAllError(null);
    // Collect all idle (ii, ci) pairs.
    const todo: Array<{ ii: number; ci: number; key: string }> = [];
    for (let ii = 0; ii < ideas.length; ii++) {
      const concepts = ideas[ii]?.concepts || [];
      for (let ci = 0; ci < concepts.length; ci++) {
        const k = conceptKey(ii, ci);
        if (!conceptRenderMap.has(k) && !pendingGenerateKeys.has(k)) {
          todo.push({ ii, ci, key: k });
        }
      }
    }
    if (todo.length === 0) {
      setGenerateAllBusy(false);
      return;
    }
    // Flood the pending set with all keys — disables per-card buttons.
    setPendingGenerateKeys((prev) => {
      const out = new Set(prev);
      for (const t of todo) out.add(t.key);
      return out;
    });
    // Fire all in parallel. Backend's worker has a Semaphore(5) so this
    // won't burst the OpenAI rate limit; we don't need to throttle here.
    const results = await Promise.allSettled(todo.map((t) =>
      renderAdStrategistConcept({
        jobId: winningIdeasJobId,
        ideaIdx: t.ii,
        conceptIdx: t.ci,
        aspectRatio: '1:1',
      }),
    ));
    // Merge successes into conceptRenderMap. Failures stay idle (user can
    // retry per-card or click Generate-all again).
    setConceptRenderMap((prev) => {
      const out = new Map(prev);
      results.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          out.set(todo[i].key, {
            status: res.value.status,
            postId: res.value.post_id,
            imageUrl: null,
          });
        }
      });
      return out;
    });
    setPendingGenerateKeys((prev) => {
      const out = new Set(prev);
      for (const t of todo) out.delete(t.key);
      return out;
    });
    // Report any errors.
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      const firstMsg = failures[0].reason instanceof Error
        ? failures[0].reason.message
        : String(failures[0].reason);
      setGenerateAllError(
        failures.length === results.length
          ? `Generate failed: ${firstMsg}`
          : `${results.length - failures.length} of ${results.length} queued — ${failures.length} failed: ${firstMsg}`,
      );
    }
    setGenerateAllBusy(false);
    setShowGenerateAllConfirm(false);
    refreshCredits();
  }, [
    winningIdeasJobId, phase2Ideas, result?.brief.ideas,
    conceptRenderMap, pendingGenerateKeys, refreshCredits,
  ]);

  // Step 5's onConfirm. Fires regenerateAdConcepts against the live job
  // (reuses cached competitor intel — no Apify re-spend; only the LLM
  // concept call runs, charged 5 credits). Updates liveResult.brief.concepts
  // in place.
  //
  // UX: advance to Step 6 IMMEDIATELY so the user gets visual feedback
  // (Step 6 shows 5 skeleton cards while regenerating=true), instead of
  // staring at a spinner inside the Generate button for 10-20s. On API
  // failure we revert to Step 5 so the user can retry.
  const handleConfirmProduct = useCallback(async (p: ProductInfo) => {
    if (!liveJobId) return;
    setRegenError(null);
    setConfirmedProduct(p);
    // Same product as last time → skip the regen, reuse cached concepts.
    if (p.name && p.name === briefProductFor) {
      setFormStep(6);
      return;
    }
    setRegenerating(true);
    setFormStep(6); // advance eagerly — Step 6 owns the skeleton render
    try {
      const newBrief = await regenerateAdConcepts({
        jobId: liveJobId,
        productName: p.name,
        productDescription: p.description,
        templateCatalog: buildTemplateCatalog(),
      });
      setLiveResult((prev) => (prev ? { ...prev, brief: newBrief } : prev));
      setBriefProductFor(p.name);
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : 'Could not generate the concepts.');
      // Revert to Step 5 so the user can fix + retry. The picker stays
      // populated with their selection; clicking Generate again re-fires.
      setFormStep(5);
    } finally {
      setRegenerating(false);
    }
  }, [liveJobId, briefProductFor]);

  // Single cleanup path used by all "leave the wizard" exits — Back button,
  // browser popstate, etc. Clears every piece of in-flight state so the
  // next entry starts fresh, then drops back to the cards view.
  const exitWizard = useCallback(() => {
    setShowingForm(false);
    setLiveResult(null);
    setLiveJobId(null);
    setConfirmedProduct(null);
    setBriefProductFor('');
    setRegenError(null);
    setError(null);
    resetFormStep();
  }, [resetFormStep]);

  // Browser back/forward — without this, the back button navigates away to
  // whatever route the user came from (typically /create when they clicked
  // "Ad Strategist" from the sidebar), abandoning their in-flight wizard.
  //
  // Approach: when the wizard opens we push a sentinel history entry. The
  // popstate fires when the user hits back, but the URL stays on
  // /ad-strategist (because we already pushed an in-place entry). We then
  // intercept and run exitWizard, dropping back to the cards view without
  // leaving the page.
  //
  // Side effect: if the user wants to ACTUALLY leave the AS page while in
  // the wizard, they need to hit back TWICE (once to exit the wizard, once
  // to leave). That's the same UX modal dialogs use; it's the right
  // tradeoff for not losing work.
  useEffect(() => {
    if (!showingForm) return;
    // Push a sentinel entry so the next back press fires popstate without
    // actually changing the URL.
    window.history.pushState({ adStrategistWizard: true }, '');
    const onPopState = () => exitWizard();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [showingForm, exitWizard]);

  const handleGenerateConcept = (concept: AdConcept) => {
    const route = concept.route && ROUTE_LABELS[concept.route] ? concept.route : 'single';
    // Resolve to the SAME real template the mockup showed, so the wizard
    // pre-selects what the user previewed (not the raw, possibly-off LLM id).
    const resolved = resolveTemplate(route, concept.template_id, `${concept.visual_direction || ''} ${concept.template_hint || ''}`);
    const payload = {
      route,
      templateId: resolved?.id ?? concept.template_id ?? null,
      productId: suggestedProductId,
      headline: concept.headline,
      subhead: concept.subhead,
      visual_direction: concept.visual_direction,
      template_hint: concept.template_hint,
    };
    try {
      sessionStorage.setItem('adStrategistConcept', JSON.stringify(payload));
    } catch { /* ignore quota errors */ }
    navigate(`/appv2?type=${route}`);
  };

  // Three mutually-exclusive views when there's no current result:
  //   1. LOADING — initial paint, while we wait for the first
  //      listAdStrategistJobs() to resolve. Prevents the "form flashes
  //      before cards" bug.
  //   2. CARDS view — recent analyses laid out as a compact list. Default
  //      landing when the user has prior runs. Top-right CTA flips to form.
  //   3. FORM view — the run form. Default landing when there are NO prior
  //      runs; otherwise revealed by the top-right CTA. When recent jobs
  //      exist, a "← Back to analyses" link lets the user cancel out.
  const hasRecent = recentJobs.length > 0;
  const showLoadingView = !result && !recentJobsLoaded;
  // showFormView now requires showingForm (= user clicked New Analysis).
  // Used to default-true when !hasRecent, which dumped empty accordion
  // shells on first-time users. Now first-time users see the empty
  // hero state and explicitly click the New Analysis CTA to start.
  const showFormView = !result && recentJobsLoaded && showingForm;
  const showCardsView = !result && recentJobsLoaded && hasRecent && !showingForm;
  // Empty hero — no recent analyses AND user hasn't started a new one.
  // First-time-user landing. Owns the New Analysis CTA + a brief
  // explainer of what the surface does.
  const showEmptyView = !result && recentJobsLoaded && !hasRecent && !showingForm;

  // Ad Strategist is tablet + desktop only — competitor analysis, the brief,
  // and the concept grid can't lay out on a phone. Gate below md (768px).
  if (isMobile) {
    return (
      <AppLayoutV2>
        <div className="min-h-[calc(100dvh-4rem)] flex flex-col items-center justify-center px-8 py-12 text-center">
          <i className="ri-computer-line text-5xl text-primary/40 mb-4" />
          <h1 className="text-lg font-bold text-dark mb-1.5">Ad Strategist needs a bigger screen</h1>
          <p className="text-sm text-dark-muted max-w-xs leading-relaxed mb-5">
            Competitor analysis, the brief, and the concept grid are built for
            tablet &amp; desktop. Open this on a larger screen to use it.
          </p>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
          >
            <i className="ri-arrow-left-line" />
            Back to create
          </button>
        </div>
      </AppLayoutV2>
    );
  }

  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* ============================================================
            HEADER BAR — title + description on the left, action
            affordance on the right. Action varies by view:
              • cards view  → "Start new analysis" primary CTA
              • form  view  → "← Back to analyses" outline button (only
                              if there ARE recent analyses to return to)
              • result view → no top-right action (the result has its own
                              banner + Back button)
            Layout is appv2-uniform: same max-width, same gap from page
            top as other AppLayoutV2-wrapped pages (My Creatives, My Brand).
            ============================================================ */}
        {/* Mobile: stacks title block above action button so the button
            doesn't wrap into the title. At sm+ everything sits on one
            row, action right-aligned. */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <i className="ri-radar-line text-primary text-2xl" />
              <h1 className="font-display font-bold text-dark text-2xl">Ad Strategist</h1>
            </div>
            <p className="text-dark-muted text-sm">
              Pull your competitors’ live ads, see what’s working, and get 5 concept directions
              engineered to beat them.
            </p>
          </div>
          {(showCardsView || showEmptyView) && (
            <button
              onClick={startAnalysis}
              title="New Analysis"
              // Matches the "+ New Creative" sidebar CTA in AppLayoutV2 — same
              // py-2 outer + w-9 h-9 icon tile, same shadow + hover-lift,
              // so the two primary CTAs in the layout feel identical in
              // weight and behaviour. Full-width on mobile (stacked
              // below the title) for an easy thumb tap.
              className="
                group flex-shrink-0 inline-flex items-center justify-center gap-2.5 py-2 pl-2 pr-5 rounded-full
                w-full sm:w-auto
                bg-primary hover:bg-primary-hover text-white
                text-sm font-bold transition-all duration-200 cursor-pointer
                shadow-md shadow-primary/25
                hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5
              "
            >
              <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
                <i className="ri-add-line text-base" />
              </span>
              <span>New Analysis</span>
            </button>
          )}
          {showFormView && hasRecent && (
            <button
              onClick={exitWizard}
              className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full w-full sm:w-auto bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Back to analyses
            </button>
          )}
        </div>

        {/* ============================================================
            LOADING — single muted skeleton row so the page doesn't flash
            the (empty) form while jobs are still being fetched.
            ============================================================ */}
        {showLoadingView && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl glass-stroke bg-white/70 px-5 py-4 flex items-center gap-3 animate-pulse"
              >
                <div className="w-9 h-9 rounded-xl bg-surface-dark/40 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 rounded bg-surface-dark/40" />
                  <div className="h-2.5 w-1/2 rounded bg-surface-dark/30" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ============================================================
            CARDS VIEW — compact list of analyses. Uses the completed-state
            chrome from /appv2's Section so the page reads as part of the
            same design family. Shows the 5 most recent by default with a
            "View all N" toggle to expand the rest. A small section header
            with a hairline divider separates it from the page header so
            it reads as "← what you ran before" rather than the page's
            primary surface.
            ============================================================ */}
        {showCardsView && (
          <>
            <div className="border-t border-surface-dark/30 pt-5 mb-3 flex items-end justify-between gap-3">
              <div>
                <h2 className="font-display font-bold text-dark text-base">
                  Previous analyses
                </h2>
                <p className="text-xs text-dark-muted mt-0.5">
                  Pick up where you left off — open one to see its brief and any ads you saved.
                </p>
              </div>
              <span className="flex-shrink-0 text-xs text-dark-muted">
                {recentJobs.length} total
              </span>
            </div>
            <AnalysisList
              jobs={recentJobs}
              onOpen={handleOpenJob}
              onRename={handleRenameJob}
              onDelete={handleDeleteJob}
            />
          </>
        )}

        {/* ============================================================
            FORM VIEW — 4-step accordion mirroring /appv2's wizard. Only
            one section is "active" at a time; previous steps collapse to
            a summary row with an Edit pill.

              Step 1  Your brand information   (brand + website + cadence)
              Step 2  Your competitors         (competitor list)
              Step 3  Competitor ads           (Apify scrape — auto-fires
                                                  on entry, shows ads)
              Step 4  Openings                 (LLM brief — snapshot/
                                                  gap/angles)

            On Step 4 Continue, the buffered result becomes the live
            `result` and the user lands in ResultsView with the product
            picker + concepts grid.
            ============================================================ */}
        {showEmptyView && (
          // First-time-user hero. Replaces the empty accordion shells
          // that previously rendered when the user landed on a fresh AS
          // surface with no past analyses. Gives a clear "what now?"
          // affordance and surfaces the validation error inline if the
          // user clicked New Analysis but is missing brand/competitors.
          <div className="rounded-2xl border border-surface-dark/30 bg-white p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-light flex items-center justify-center">
              <i className="ri-radar-line text-primary text-3xl" />
            </div>
            <h2 className="font-display font-bold text-dark text-xl mb-2">
              No analyses yet
            </h2>
            <p className="text-dark-muted text-sm max-w-md mx-auto mb-5">
              Pull your competitors’ live ads, see what they’re running, and get 5 concept
              directions engineered to beat them. We’ll use your active brand and the
              competitors you’ve saved.
            </p>
            {error && (
              <div className="mb-4 max-w-md mx-auto rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 text-left">
                {error}
              </div>
            )}
            <button
              onClick={startAnalysis}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all"
            >
              <i className="ri-add-line" />
              Start your first analysis
            </button>
          </div>
        )}

        {showFormView && (
          <div className="space-y-1">
            {/* Surface-level error (auth / network) — shown above the
                stepper so it's always visible regardless of which step
                has focus. */}
            {error && (
              <div className="mb-3 rounded-xl p-4 text-sm bg-red-50 border border-red-200 text-red-700">
                <div className="flex items-start gap-2">
                  <i className="mt-0.5 ri-error-warning-line" />
                  <div className="flex-1">{error}</div>
                </div>
              </div>
            )}

            {/* Steps 1 (Your brand information) and 2 (Your competitors)
                are hidden in the streamlined flow. Brand + competitor
                data are pulled automatically from My Brand and My
                Competitors. The "Start new analysis" button validates
                that both exist and jumps directly to Step 3, which
                fires the Apify scrape via the handleRun effect. Code
                kept (wrapped in {false &&}) so the full-input flow can
                be brought back if we ever need it for power users. */}
            {false && (
            <>
            <AccordionStep
              step={1}
              title="Your brand information"
              icon="ri-user-star-line"
              description="What you're analyzing for. Brand name + your store so we can pull your best-seller as the product to feature."
              summary={brandName ? `${brandName}${brandWebsite ? ` · ${brandWebsite}` : ''}` : 'Not set yet'}
              state={stepState(1)}
              onEdit={() => setFormStep(1)}
              cta={
                <button
                  onClick={() => setFormStep(2)}
                  disabled={!brandName.trim()}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Continue <i className="ri-arrow-right-line" />
                </button>
              }
            >
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-dark mb-1.5">Your brand</label>
                    <input
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="e.g. Glossier"
                      className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-dark mb-1.5">
                      Your Facebook page <span className="text-dark-muted font-normal">(optional — to include your own ads)</span>
                    </label>
                    <input
                      value={brandQuery}
                      onChange={(e) => setBrandQuery(e.target.value)}
                      placeholder="facebook.com/yourbrand"
                      className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-dark mb-1.5">
                    Your store website <span className="text-dark-muted font-normal">(we auto-pick your best-seller / homepage hero product to feature)</span>
                  </label>
                  <input
                    value={brandWebsite}
                    onChange={(e) => setBrandWebsite(e.target.value)}
                    placeholder="yourbrand.com"
                    className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-dark mb-1.5">
                    What your brand is about <span className="text-dark-muted font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={brandDescription}
                    onChange={(e) => setBrandDescription(e.target.value)}
                    rows={2}
                    placeholder="A short positioning blurb — pulled from My Brand if you've set it up."
                    className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none resize-none"
                  />
                </div>
                <div className="md:max-w-xs">
                  <label className="block text-xs font-semibold text-dark mb-1.5">
                    Re-run automatically <span className="text-dark-muted font-normal">(optional)</span>
                  </label>
                  <select
                    value={rerunIntervalDays ?? ''}
                    onChange={(e) =>
                      setRerunIntervalDays(e.target.value === '' ? null : parseInt(e.target.value, 10))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none bg-white"
                  >
                    <option value="">No — manual only</option>
                    <option value="7">Every 7 days</option>
                    <option value="14">Every 14 days</option>
                    <option value="20">Every 20 days</option>
                  </select>
                </div>
              </div>
            </AccordionStep>

            {/* ── Step 2 ── Your competitors ───────────────────────────── */}
            <AccordionStep
              step={2}
              title="Your competitors"
              icon="ri-team-line"
              description={
                savedCompetitors.length === 0
                  ? 'Add competitors to track. Once saved, you can pick which to include here.'
                  : 'Pick which saved competitors to include in this analysis.'
              }
              summary={
                validCompetitors.length === 0
                  ? savedCompetitors.length === 0
                    ? 'None added yet'
                    : 'None selected'
                  : validCompetitors.length === 1
                    ? validCompetitors[0].label || validCompetitors[0].query
                    : `${validCompetitors.length} selected`
              }
              state={stepState(2)}
              onEdit={() => setFormStep(2)}
              cta={
                <button
                  onClick={() => { setError(null); setLiveResult(null); setFormStep(3); }}
                  disabled={validCompetitors.length === 0}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Get competitor ads <i className="ri-arrow-right-line" />
                </button>
              }
            >
              <SuggestCompetitorsPanel
                suggestions={suggestions}
                loading={suggestionsLoading}
                error={suggestionsError}
                fromCache={suggestionsFromCache}
                usedProductSignals={suggestionsUsedProducts}
                onSuggest={handleSuggestCompetitors}
                onAdd={handleAddSuggestion}
                pickedQueries={savedCompetitors
                  .filter((c) => selectedSavedIds.has(c.id))
                  .map((c) => c.query)}
              />

              {savedLoading ? (
                <div className="flex items-center gap-2 py-4 text-dark-muted text-sm">
                  <i className="ri-loader-4-line animate-spin" /> Loading your competitors…
                </div>
              ) : savedCompetitors.length === 0 ? (
                <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-6 text-center">
                  <i className="ri-spy-line text-2xl text-dark-muted/60" />
                  <p className="text-sm font-semibold text-dark mt-2">No competitors saved yet</p>
                  <p className="text-xs text-dark-muted mt-0.5 mb-3">
                    Use the Suggest panel above OR head to the Competitors page to add them by URL.
                  </p>
                  <a
                    href="/competitors"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold cursor-pointer transition-all"
                  >
                    <i className="ri-add-line" /> Manage competitors
                  </a>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-dark-muted uppercase tracking-wide">
                      Your saved competitors · {selectedSavedIds.size}/{savedCompetitors.length} selected
                    </p>
                    <a
                      href="/competitors"
                      className="text-xs font-bold text-primary hover:underline cursor-pointer"
                    >
                      Manage →
                    </a>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {savedCompetitors.map((c) => {
                      const isSelected = selectedSavedIds.has(c.id);
                      const hasAds = c.active_ads_count > 0;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleSavedCompetitor(c.id)}
                          className={`text-left rounded-lg border-2 px-3 py-2.5 transition-colors cursor-pointer ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-surface-dark/30 bg-white hover:border-primary/40'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected
                                ? 'border-primary bg-primary'
                                : 'border-surface-dark/50'
                            }`}>
                              {isSelected && <i className="ri-check-line text-white text-xs" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-bold text-dark truncate">{c.label}</p>
                                {hasAds ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 flex-shrink-0">
                                    {c.active_ads_count}{c.active_ads_count >= 20 ? '+' : ''}
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-surface-dark/30 text-dark-muted flex-shrink-0">
                                    0
                                  </span>
                                )}
                              </div>
                              {c.facebook_handle && (
                                <p className="text-xs text-dark-muted truncate">facebook.com/{c.facebook_handle}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </AccordionStep>
            </>
            )}

            {/* ── Step 3 ── Competitor ads ─────────────────────────────── */}
            {/* Skipped entirely when the user opted to run without
                competitors — nothing to pull from the Ad Library. The
                run-in-flight state is shown inside Step 4 below instead. */}
            {hasCompetitors && (
              <AccordionStep
                step={3}
                title="Competitor ads"
                icon="ri-image-2-line"
                description="Live ads pulled from the Meta Ad Library."
                summary={
                  liveResult
                    ? `${liveResult.counts.competitor_ads} ad${liveResult.counts.competitor_ads === 1 ? '' : 's'} across ${liveResult.gathered.competitors.length} competitor${liveResult.gathered.competitors.length === 1 ? '' : 's'}`
                    : running ? 'Pulling…' : 'Not pulled yet'
                }
                state={stepState(3)}
                onEdit={() => setFormStep(3)}
                cta={
                  <button
                    onClick={() => setFormStep(4)}
                    disabled={running || !liveResult}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    See the openings <i className="ri-arrow-right-line" />
                  </button>
                }
              >
                {running && !liveResult ? (
                  <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/20 p-4">
                    <i className="ri-loader-4-line text-primary text-xl animate-spin flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-dark">{progressStages[stageIdx]}</p>
                      <p className="text-xs text-dark-muted mt-0.5">This can take a minute or two — analyzing live ads.</p>
                    </div>
                  </div>
                ) : liveResult ? (
                  <CompetitorAdsPreview gathered={liveResult.gathered} />
                ) : (
                  <p className="text-sm text-dark-muted">Waiting to start…</p>
                )}
              </AccordionStep>
            )}

            {/* ── Step 4 ── Strategic angles (Phase 1 brief) ───────────── */}
            {/* Title renamed from "Openings to beat them" — Phase 2 (Step 6)
                is now the place where "winning ideas" are produced. Step 4
                shows the brief.differentiation_angles read-only so the user
                understands the strategy before picking products. */}
            <AccordionStep
              step={4}
              title="Strategic angles"
              icon="ri-lightbulb-flash-line"
              description={
                hasCompetitors
                  ? "Where competitors aren't showing up — and how to take advantage."
                  : 'Strategic angles built from your brand identity.'
              }
              summary={
                liveResult?.brief.differentiation_angles && liveResult.brief.differentiation_angles.length > 0
                  ? `${liveResult.brief.differentiation_angles.length} angle${liveResult.brief.differentiation_angles.length === 1 ? '' : 's'} identified`
                  : running ? 'Building…' : 'Brief ready'
              }
              state={
                // When Step 3 is hidden (no-competitors run), formStep=3
                // also lights up Step 4 so the user has a visible active
                // section to wait inside.
                !hasCompetitors && formStep === 3
                  ? 'active'
                  : stepState(4)
              }
              onEdit={() => setFormStep(4)}
              cta={
                <button
                  onClick={handleStep4Continue}
                  disabled={!liveResult}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Pick products to feature <i className="ri-arrow-right-line" />
                </button>
              }
            >
              {running && !liveResult ? (
                <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/20 p-4">
                  <i className="ri-loader-4-line text-primary text-xl animate-spin flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark">{progressStages[stageIdx]}</p>
                    <p className="text-xs text-dark-muted mt-0.5">
                      {hasCompetitors
                        ? 'This can take a minute or two — analyzing live ads.'
                        : 'Building your brand strategy brief…'}
                    </p>
                  </div>
                </div>
              ) : liveResult ? (
                <OpeningsPreview brief={liveResult.brief} />
              ) : (
                <p className="text-sm text-dark-muted">Waiting for the analysis to finish…</p>
              )}
            </AccordionStep>

            {/* ── Step 5 ── Product selection ──────────────────────────── */}
            {/* New flow: user picks the products they want featured (All
                Products by default). Phase 2 LLM call uses this selection
                to ground its 20 concepts. Mirrors the Ad Pilot picker. */}
            <AccordionStep
              step={5}
              title="Pick products to feature"
              icon="ri-shopping-bag-3-line"
              description="The strategist will fan your selection across 20 unique ad concepts."
              summary={
                phase2Selection.allSelected
                  ? 'Using your full catalog'
                  : phase2Selection.selectedIds.length > 0
                    ? `${phase2Selection.selectedIds.length} product${phase2Selection.selectedIds.length === 1 ? '' : 's'} selected`
                    : 'Nothing selected yet'
              }
              state={stepState(5)}
              onEdit={() => setFormStep(5)}
              cta={
                <button
                  onClick={handleStep5Continue}
                  disabled={
                    phase2Building ||
                    (!phase2Selection.allSelected && phase2Selection.selectedIds.length === 0)
                  }
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {phase2Building ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Generating ideas…
                    </>
                  ) : (
                    <>
                      Generate winning ideas <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              }
            >
              <ProductCatalogPicker
                includeAllToggle={true}
                includeCollections={true}
                initialAllSelected={true}
                onSelectionChange={setPhase2Selection}
              />
              {phase2Error && (
                <p className="text-sm text-red-600 font-medium mt-3">
                  {phase2Error}
                </p>
              )}
            </AccordionStep>

            {/* ── Step 6 ── Winning Ideas (Phase 2 output) + per-card Generate */}
            {/* 5 idea buckets × 4 distinct concepts each. Each card has its
                own Generate button (1 credit). "Generate all N" button at
                top-right + bottom-center fires render-concept × N in parallel.
                Renders land in MyCreatives + reflect inline on each card. */}
            <AccordionStep
              step={6}
              title="Winning Ideas"
              icon="ri-magic-line"
              description="5 strategic angles × 4 distinct takes each = 20 ads."
              summary={
                phase2Ideas
                  ? `${phase2Ideas.reduce((n, i) => n + (i.concepts?.length ?? 0), 0)} concepts ready`
                  : phase2Building ? 'Drafting…' : 'Pick products first'
              }
              state={stepState(6)}
              onEdit={() => setFormStep(6)}
              cta={
                // Generate-all lives in the prominent bar at the TOP of the
                // concepts (below) so it's reachable without hunting the header;
                // the header just reflects the done-state.
                phase2Ideas && idleConceptCount === 0 ? (
                  <div className="text-sm text-emerald-700 font-bold flex items-center gap-1.5">
                    <i className="ri-check-line" />
                    All concepts generated
                  </div>
                ) : null
              }
            >
              {phase2Building ? (
                <div className="space-y-3">
                  <p className="text-sm text-dark-muted flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Drafting 20 unique concepts from your strategic angles…
                  </p>
                  <div className="grid lg:grid-cols-2 gap-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-surface-dark/40 bg-white p-3"
                      >
                        <div className="h-5 w-32 rounded bg-surface-dark/40 mb-2 animate-pulse" />
                        <div className="h-4 w-full rounded bg-surface-dark/30 mb-1 animate-pulse" />
                        <div className="h-4 w-3/4 rounded bg-surface-dark/30 animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : phase2Ideas ? (
                <>
                  {/* Prominent Generate-all — pinned at the TOP of the concepts
                      so it's the first thing in reach (no scrolling to a footer),
                      and the single source of the action (dedups the old header
                      CTA + floating pill). */}
                  {idleConceptCount > 0 && (
                    <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-primary/5 border border-primary/25 px-4 py-3">
                      <span className="text-sm font-bold text-dark inline-flex items-center gap-1.5">
                        <i className="ri-magic-line text-primary" />
                        {idleConceptCount} concept{idleConceptCount === 1 ? '' : 's'} ready to render
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowGenerateAllConfirm(true)}
                        disabled={generateAllBusy}
                        className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {generateAllBusy ? (
                          <><i className="ri-loader-4-line animate-spin" /> Queueing…</>
                        ) : (
                          <><i className="ri-magic-line" /> Generate all {idleConceptCount} ({idleConceptCount} cr)</>
                        )}
                      </button>
                    </div>
                  )}
                  {orphanedRenderCount > 0 && (
                    <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm flex items-center gap-2 text-amber-800">
                      <i className="ri-information-line" />
                      <span>
                        <strong>{orphanedRenderCount} earlier render{orphanedRenderCount === 1 ? '' : 's'}</strong>
                        {' '}from this analysis are saved in
                        {' '}<a href="/gallery" className="font-bold underline">My Creatives</a>{' '}
                        but couldn't be matched to specific slots below
                        (from before today's update).
                      </span>
                    </div>
                  )}
                  <WinningIdeasGrid
                    ideas={phase2Ideas}
                    products={savedProducts}
                    renderStateFor={(ii, ci) => {
                      const k = conceptKey(ii, ci);
                      if (pendingGenerateKeys.has(k)) return { status: 'pending' };
                      const v = conceptRenderMap.get(k);
                      if (!v) return { status: 'idle' };
                      if (v.status === 'rendered') return {
                        status: 'rendered',
                        postId: v.postId,
                        imageUrl: v.imageUrl,
                      };
                      return { status: 'queued', postId: v.postId };
                    }}
                    generateDisabled={generateAllBusy}
                    onGenerate={handleGenerateConceptForCard}
                  />
                </>
              ) : (
                <p className="text-sm text-dark-muted">
                  Click Generate winning ideas above to populate this section.
                </p>
              )}
              {phase2Error && (
                <p className="text-sm text-red-600 font-medium mt-3">
                  {phase2Error}
                </p>
              )}
              {generateAllError && (
                <p className="text-sm text-red-600 font-medium mt-3">
                  {generateAllError}
                </p>
              )}
            </AccordionStep>
          </div>
        )}

        {/* ---- Results ---- */}
        {result && (
          <>
            {/* Archived opens get just two solid actions — Back to the recent
                list, or start a fresh analysis. The old Snapshot banner +
                "Refresh concepts" card were removed (Jul 22): running a NEW
                analysis is the intended path, not re-drafting in place. */}
            {viewingArchived && (
              <div className="mb-4 flex items-center justify-between gap-3">
                <button
                  onClick={() => { setResult(null); setJobId(null); setViewingArchived(false); }}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-sm font-bold transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line" /> Back
                </button>
                <button
                  onClick={proceedToRun}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
                >
                  <i className="ri-add-line" /> New analysis
                </button>
              </div>
            )}
            <ResultsView
              result={result}
              jobId={jobId}
              savedProducts={savedProducts}
              brandLogoUrl={brandLogoUrl}
              viewingArchived={viewingArchived}
              archivedSavedPosts={jobId ? postsByJob[jobId] || [] : []}
              // Hide brief sections on live-run handoff — the user just saw
              // them in the stepper's Step 4 (Openings). Archived opens
              // didn't go through the stepper, so they DO show the brief.
              briefShownInStepper={!viewingArchived}
              onReset={() => { setResult(null); setJobId(null); setViewingArchived(false); }}
              onGenerate={handleGenerateConcept}
              // Per-concept render plumbing — same state + handlers the
              // wizard Step 6 uses, threaded through so archived views
              // get the same Generate-per-card + Generate-all UX.
              renderStateFor={(ii, ci) => {
                const k = conceptKey(ii, ci);
                if (pendingGenerateKeys.has(k)) return { status: 'pending' };
                const v = conceptRenderMap.get(k);
                if (!v) return { status: 'idle' };
                if (v.status === 'rendered') return {
                  status: 'rendered',
                  postId: v.postId,
                  imageUrl: v.imageUrl,
                };
                return { status: 'queued', postId: v.postId };
              }}
              generateDisabled={generateAllBusy}
              onGenerateConcept={handleGenerateConceptForCard}
              onOpenGenerateAll={() => setShowGenerateAllConfirm(true)}
              generateAllBusy={generateAllBusy}
              idleConceptCount={idleConceptCount}
              orphanedRenderCount={orphanedRenderCount}
            />
          </>
        )}
      </div>
      {showNoCompetitorsModal && (
        <NoCompetitorsModal
          onAddCompetitors={() => {
            setShowNoCompetitorsModal(false);
            navigate('/competitors');
          }}
          onRunAnyway={runWithoutCompetitors}
          onClose={() => setShowNoCompetitorsModal(false)}
        />
      )}
      {showRecencyModal && runPrecheck && (
        <RecencyModal
          precheck={runPrecheck}
          onOpenLast={() => {
            setShowRecencyModal(false);
            // The most-recent analysis is always at the top of recentJobs
            // (the list is ordered created_at DESC). Open it as an archive
            // view — same path as clicking the row in the recent list.
            const latest = recentJobs[0];
            if (latest) {
              void handleOpenJob(latest.id);
            }
          }}
          onRunAnyway={proceedToRun}
          onClose={() => setShowRecencyModal(false)}
        />
      )}
      {showGenerateAllConfirm && (
        <GenerateAllConfirmModal
          count={idleConceptCount}
          busy={generateAllBusy}
          onConfirm={() => void handleGenerateAll()}
          onClose={() => setShowGenerateAllConfirm(false)}
        />
      )}
    </AppLayoutV2>
  );
}

// Generate-all confirm modal — surfaces the credit cost before bursting
// 20 (or however many idle) renders. Mirrors the design language of the
// other AS modals (RecencyModal, NoCompetitorsModal).
function GenerateAllConfirmModal({
  count,
  busy,
  onConfirm,
  onClose,
}: {
  count: number;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-primary-light text-primary flex items-center justify-center text-2xl">
            <i className="ri-magic-line" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-dark text-lg">
              Generate all {count} ads
            </h3>
            <p className="text-sm text-dark-muted">
              {count} credit{count === 1 ? '' : 's'} — about $0.04 per ad on the backend
            </p>
          </div>
        </div>
        <div className="text-sm text-dark-muted leading-relaxed mb-5 space-y-2">
          <p>
            Each ad will start rendering in parallel. The cards below will flip
            from <strong>Rendering…</strong> to the finished thumbnail as each
            one lands (~30-60 seconds each).
          </p>
          <p>
            All renders also save to <strong>My Creatives</strong>. Open any
            card to download, publish, or reimagine.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-full border border-surface-dark/40 text-dark text-sm font-bold hover:bg-surface/60 transition-colors disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors disabled:opacity-50 cursor-pointer inline-flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Queueing…
              </>
            ) : (
              <>
                Generate all {count} <span className="opacity-70">({count}cr)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal shown when the user starts an analysis with zero saved competitors.
// Two clear actions — add some first (recommended) or proceed without — plus
// an Escape/backdrop dismiss. The wording avoids implying competitors are
// required: the analysis still produces a brand brief + concept directions,
// just with less competitive context to differentiate against.
function NoCompetitorsModal({
  onAddCompetitors,
  onRunAnyway,
  onClose,
}: {
  onAddCompetitors: () => void;
  onRunAnyway: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="no-competitors-title"
    >
      <div
        className="relative w-full max-w-md rounded-2xl glass-stroke bg-white shadow-2xl shadow-primary/15 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <i className="ri-radar-line text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="no-competitors-title" className="font-display font-bold text-dark text-lg mb-1">
              Sharper with competitors
            </h3>
            <p className="text-dark-muted text-sm leading-relaxed">
              Ad Strategist works best when it can compare your brand against
              live competitor ads. You can still run an analysis without any —
              the brief and concepts will lean on your brand identity alone.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onAddCompetitors}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
          >
            <i className="ri-add-line" />
            Add competitors first
          </button>
          <button
            type="button"
            onClick={onRunAnyway}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-sm font-bold transition-all cursor-pointer"
          >
            Run without competitors
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center px-5 py-2 text-sm text-dark-muted hover:text-dark transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal shown when the user starts a new analysis WITHIN 7 days of their
// last run for the same brand. Competitors typically refresh their ads
// weekly, so a rerun this soon will work from the same Apify-cached data.
// The freshness nudge in the brief prompt pushes the LLM past first-pass
// angles, but the user should opt INTO paying credits with full info.
function RecencyModal({
  precheck,
  onOpenLast,
  onRunAnyway,
  onClose,
}: {
  precheck: AdStrategistRunPrecheck;
  onOpenLast: () => void;
  onRunAnyway: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const days = precheck.days_since_last ?? 0;
  const dayLabel = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
  const cost = precheck.credit_cost;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="recency-modal-title"
    >
      <div
        className="relative w-full max-w-md rounded-2xl glass-stroke bg-white shadow-2xl shadow-primary/15 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="recency-modal-title" className="font-display font-bold text-dark text-lg mb-1">
              You ran this {dayLabel}
            </h3>
            <p className="text-dark-muted text-sm leading-relaxed">
              Competitors typically refresh their ads weekly, so a rerun this
              soon will work from the same data. We&apos;ll push the LLM to find
              fresh angles, but expect overlap with the last brief.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onOpenLast}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer"
          >
            <i className="ri-folder-open-line" />
            Open the last analysis
          </button>
          <button
            type="button"
            onClick={onRunAnyway}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-sm font-bold transition-all cursor-pointer"
          >
            Run anyway
            {cost > 0 && (
              <span className="text-xs text-dark-muted">
                · {cost} credit{cost === 1 ? '' : 's'}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center px-5 py-2 text-sm text-dark-muted hover:text-dark transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// AnalysisList — stacked list of compact analysis rows. Shows the 5 most
// recent by default + a "View all N" toggle to expand the rest. Each row
// uses the same completed-state chrome as /appv2's accordion Section so
// the page reads as part of the same design family.
function AnalysisList({
  jobs,
  onOpen,
  onRename,
  onDelete,
}: {
  jobs: AdStrategistJob[];
  onOpen: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const VISIBLE_BY_DEFAULT = 5;
  const hiddenCount = Math.max(0, jobs.length - VISIBLE_BY_DEFAULT);
  const visible = showAll ? jobs : jobs.slice(0, VISIBLE_BY_DEFAULT);

  return (
    <div className="space-y-3">
      {visible.map((j) => (
        <AnalysisRow key={j.id} job={j} onOpen={onOpen} onRename={onRename} onDelete={onDelete} />
      ))}
      {hiddenCount > 0 && (
        <div className="flex justify-center pt-1">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-xs font-bold transition-all cursor-pointer"
          >
            {showAll ? (
              <><i className="ri-arrow-up-s-line" /> Show fewer</>
            ) : (
              <><i className="ri-arrow-down-s-line" /> View all {jobs.length}</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// AnalysisRow — one compact full-width row per saved analysis. Whole row
// is a click target when ready. Pencil for rename (stopPropagation so it
// doesn't trigger Open). Mirrors the completed-state Section chrome from
// /appv2: glass-stroke gradient border + bg-white/85 + subtle hover lift.
function AnalysisRow({
  job,
  onOpen,
  onRename,
  onDelete,
}: {
  job: AdStrategistJob;
  onOpen: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const confirm = useConfirm();

  // Ad-Pilot source detection — these rows are spawned weekly by the cron
  // worker, not user-initiated. The backend stamps input.source='ad_pilot'
  // + input.ad_pilot_batch_id + input.ad_pilot_week_index.
  const inputAny = job.input as {
    brand_name?: string;
    brand_query?: string;
    competitors?: { label?: string; query?: string }[];
    source?: string;
    ad_pilot_batch_id?: string;
    ad_pilot_week_index?: number;
    week_starts_at?: string;
  } | undefined;
  const isAdPilot = inputAny?.source === 'ad_pilot' || !!inputAny?.ad_pilot_batch_id;

  // Build a meaningful auto-name. For Ad-Pilot rows, the backend already
  // stamps the name as "Ad Pilot · Week of {date}" — so this fallback
  // basically never fires for those. Examples for non-AP:
  //   "Collab Culture, Tech-Material & 3 more"   (Phase 2 with ideas)
  //   "vs Adidas, Nike & 1 more"                  (Phase 1 with competitors)
  //   "Niobiowla analysis"                        (no usable signal)
  const fallbackName = (j: AdStrategistJob): string => {
    const angles = (j.angle_names || []).filter((a): a is string => typeof a === 'string' && a.trim().length > 0);
    if (angles.length > 0) {
      const head = angles.slice(0, 2).map((a) => a.trim());
      const rest = angles.length - head.length;
      return rest > 0 ? `${head.join(', ')} & ${rest} more` : head.join(', ');
    }
    const comps = (inputAny?.competitors || []).map((c) => c.label || c.query || '').filter((s) => s.length > 0);
    if (comps.length > 0) {
      const head = comps.slice(0, 2);
      const rest = comps.length - head.length;
      return rest > 0 ? `vs ${head.join(', ')} & ${rest} more` : `vs ${head.join(', ')}`;
    }
    const brand = inputAny?.brand_name || inputAny?.brand_query || 'Analysis';
    return `${brand} analysis`;
  };

  // Full date + time so the user can disambiguate multiple runs in the same
  // day. e.g. "Jun 8, 2026 · 3:45 PM"
  const formattedDate = job.created_at
    ? new Date(job.created_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      }).replace(/,\s*(?=\d{1,2}:)/, ' · ')
    : 'Unknown date';

  const compCount = ((job.input as { competitors?: unknown[] } | undefined)?.competitors?.length) ?? 0;
  const displayName = job.name || fallbackName(job);
  const isReady = job.status === 'ready';
  const isFailed = job.status === 'failed';
  const isAutoRerun = !!job.parent_id;

  const interactiveHover = isReady ? 'hover:bg-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10 cursor-pointer' : 'cursor-default';
  const rowShell = `group relative rounded-2xl glass-stroke bg-white/85 shadow-md shadow-primary/5 transition-all duration-300 ease-out ${interactiveHover}`;

  const handleRowClick = () => { if (isReady) onOpen(job.id); };
  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftName(displayName);
    setIsEditing(true);
  };
  // Confirm before deleting — destructive + permanent (the row can't be
  // recovered; saved ads stay in My Creatives but lose the analysis link).
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await confirm({
      title: `Delete "${displayName}"?`,
      message: "This can't be undone. Any ads you saved from this analysis stay in My Creatives.",
      confirmLabel: 'Delete analysis',
      destructive: true,
    });
    if (ok) onDelete(job.id);
  };

  return (
    <div
      role={isReady ? 'button' : undefined}
      tabIndex={isReady ? 0 : -1}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (!isReady) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(job.id);
        }
      }}
      className={`${rowShell} px-5 py-4 flex items-center gap-3`}
      aria-label={isReady ? `Open analysis ${displayName}` : displayName}
    >
      {/* Icon tile — Ad-Pilot rows get a flight-takeoff icon + accent tint
          so they're at-a-glance distinguishable from user-initiated runs. */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
        isAdPilot ? 'bg-accent/15 text-accent' : 'bg-primary/10 text-primary'
      }`}>
        <i className={`${isAdPilot ? 'ri-flight-takeoff-line' : 'ri-radar-line'} text-lg`} />
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            autoFocus
            value={draftName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => {
              const trimmed = draftName.trim();
              if (trimmed && trimmed !== (job.name || '')) onRename(job.id, trimmed);
              setIsEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            maxLength={120}
            className="w-full px-2 py-1 rounded-lg border border-primary text-sm font-display font-bold text-dark focus:outline-none"
          />
        ) : (
          <div className="flex items-center gap-1.5">
            <h3 className="font-display font-bold text-dark text-sm truncate" title={displayName}>
              {displayName}
            </h3>
            {isAdPilot && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-bold">
                <i className="ri-flight-takeoff-line text-[10px]" />
                Ad Pilot
              </span>
            )}
            {/* Row-action affordances — hidden until row hover so they
                don't add visual noise at rest. Both stopPropagation so
                they don't trigger the row's Open handler. */}
            <button
              type="button"
              onClick={startRename}
              title="Rename"
              className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-dark-muted hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
            >
              <i className="ri-pencil-line text-xs" />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              title="Delete analysis"
              className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-dark-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-dark-muted">
          <span>{formattedDate}</span>
          <span aria-hidden>·</span>
          <span>{compCount} competitor{compCount === 1 ? '' : 's'}</span>
          {job.rerun_interval_days && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-bold">
              <i className="ri-repeat-line text-xs" />
              Every {job.rerun_interval_days}d
            </span>
          )}
          {isAutoRerun && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-dark/40 text-dark-muted text-[10px] font-bold">
              <i className="ri-refresh-line text-xs" />
              Auto re-run
            </span>
          )}
        </div>
      </div>

      {/* Right — status pill OR Open hint (mirrors the Edit pill in Section.tsx). */}
      <div className="flex-shrink-0 flex items-center gap-2">
        {isFailed ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-100">
            <i className="ri-error-warning-line" /> Failed
          </span>
        ) : !isReady ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold border border-amber-100">
            <i className="ri-loader-4-line animate-spin" /> In progress
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-surface-dark/40 text-dark-muted group-hover:border-primary/40 group-hover:text-primary group-hover:bg-primary/5 text-xs font-semibold transition-colors">
            <i className="ri-arrow-right-line text-xs" />
            Open
          </span>
        )}
      </div>
    </div>
  );
}

interface ProductInfo {
  imageUrl: string;
  name: string;
  description: string;
  source: 'website' | 'library';
}

function ResultsView({
  result,
  jobId,
  savedProducts,
  brandLogoUrl,
  viewingArchived,
  archivedSavedPosts,
  briefShownInStepper,
  onReset,
  onGenerate,
  renderStateFor,
  generateDisabled,
  onGenerateConcept,
  onOpenGenerateAll,
  generateAllBusy,
  idleConceptCount,
  orphanedRenderCount,
}: {
  result: AdStrategistResult;
  jobId: string | null;
  savedProducts: UserProduct[];
  brandLogoUrl: string;
  /** True when the user opened a past analysis from the Recent list. Locks
   *  the regenerate-concepts path when concepts already exist (snapshot
   *  view). When false (a fresh live run), behaves as before. */
  viewingArchived: boolean;
  /** Saved posts linked to THIS analysis via source_job_id. Only used in
   *  the locked archived state to render the "Saved ads from this
   *  analysis" grid. Empty for live runs (which show the live AiMagicCards
   *  with auto-render). */
  archivedSavedPosts: Post[];
  /** When true (live-run handoff from the stepper), skip rendering the
   *  brief sections (brand snapshot / competitor landscape / gap / angles)
   *  — the user just saw them in Step 4 of the stepper. Archived opens
   *  set this false and DO render the brief inline. */
  briefShownInStepper: boolean;
  onReset: () => void;
  onGenerate: (c: AdConcept) => void;
  /** Per-card render state lookup. Same shape as the wizard Step 6
   *  WinningIdeasGrid prop. */
  renderStateFor: (ideaIdx: number, conceptIdx: number) => CardRenderState;
  /** Disable per-card Generate buttons while Generate-all is bursting. */
  generateDisabled: boolean;
  /** Fires render-concept for ONE slot. */
  onGenerateConcept: (ideaIdx: number, conceptIdx: number) => void;
  /** Open the Generate-all confirm modal. */
  onOpenGenerateAll: () => void;
  generateAllBusy: boolean;
  /** Number of concepts not yet generated — drives the "Generate all N"
   *  CTA label. 0 = everything's already rendered (CTA hidden). */
  idleConceptCount: number;
  /** Count of orphaned renders (in My Creatives, no slot link). Shown as
   *  an amber badge above the grid so the user knows their credits weren't
   *  wasted on pre-fix renders. */
  orphanedRenderCount: number;
}) {
  const { gathered, counts } = result;
  const candidates = result.product_candidates || [];
  // Brief lives in state so we can swap it when the user picks a product
  // different from the auto best-seller (regenerateAdConcepts re-runs the
  // brief against the SAME gathered intel + the new featured product).
  const [briefState, setBriefState] = useState<AdBrief>(result.brief);
  // Which product the current concepts were written for. Empty initially —
  // the run no longer generates concepts; the first Generate-click always
  // fires the concepts call. Subsequent clicks for the same product skip it.
  const [briefProductFor, setBriefProductFor] = useState<string>('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [confirmedProduct, setConfirmedProduct] = useState<ProductInfo | null>(null);

  const handleConfirm = async (p: ProductInfo) => {
    setRegenError(null);
    // Same product as last time → skip regen, reuse the existing concepts.
    if (!jobId || !p.name || p.name === briefProductFor) {
      setConfirmedProduct(p);
      return;
    }
    setRegenerating(true);
    try {
      const newBrief = await regenerateAdConcepts({
        jobId,
        productName: p.name,
        productDescription: p.description,
        templateCatalog: buildTemplateCatalog(),
      });
      setBriefState(newBrief);
      setBriefProductFor(p.name);
      setConfirmedProduct(p);
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : 'Could not refresh the concepts.');
    } finally {
      setRegenerating(false);
    }
  };
  const brief = briefState;
  // Locked view = the user opened a past analysis that ALREADY had concepts
  // when it was first cached. In that case we hide the picker + the live
  // AiMagicCard renders (which would re-fire gpt-image-1) and show the
  // concept text grid + saved-ads grid. No re-generate path.
  //
  // CRITICAL: we read this from the ORIGINAL `result` prop, NOT from the
  // mutable `briefState`. Otherwise a Generate-on-archived (empty-concepts
  // case) would write concepts into briefState → flip lockedArchived true
  // → the user never sees the AI Magic cards they just paid for. Reading
  // from `result.brief` snapshots the "was this archived-with-concepts?"
  // question to view-open time, where it belongs.
  const lockedArchived = viewingArchived && Array.isArray(result.brief.concepts) && result.brief.concepts.length > 0;
  // When revisiting a past AS run that has Winning Ideas, the ONLY thing
  // to show is the Winning Ideas grid + Refresh Concepts CTA. Brand
  // snapshot / competitor landscape / gap analysis / product picker are
  // legacy from when reruns started from product selection — not how the
  // app works anymore. For live runs (briefShownInStepper=true) the
  // wizard already showed the brief in Step 4, so it stays hidden there
  // too. Net: those sections only render in the rare "archived job that
  // has NO brief.ideas" edge case (i.e. a really old job).
  const hideLegacyBrief = briefShownInStepper || (viewingArchived && (brief.ideas?.length ?? 0) > 0);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-dark-muted">
          Analyzed {counts.competitor_ads} competitor ad{counts.competitor_ads === 1 ? '' : 's'}.
        </p>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1 text-primary text-sm font-semibold hover:underline cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> New analysis
        </button>
      </div>

      {/* Brand snapshot — legacy. Hidden on stepper handoff AND on
          archived views with Winning Ideas. */}
      {!hideLegacyBrief && brief.brand_snapshot && (
        <Section title="Your brand right now" icon="ri-user-star-line">
          <p className="text-sm text-dark leading-relaxed">{brief.brand_snapshot}</p>
        </Section>
      )}

      {/* Competitor landscape — legacy. Same visibility rule. */}
      {!hideLegacyBrief && brief.competitor_landscape && brief.competitor_landscape.length > 0 && (
        <Section title="What your competitors are running" icon="ri-sword-line">
          <div className="space-y-4">
            {brief.competitor_landscape.map((c, i) => {
              const block = gathered.competitors.find(
                (b) => b.label.toLowerCase().includes((c.competitor || '').toLowerCase()) ||
                       (c.competitor || '').toLowerCase().includes(b.label.toLowerCase()),
              );
              const thumbs = (block?.ads || []).filter((a) => a.media_cached_url).slice(0, 5);
              return (
                <div key={i} className="rounded-xl border border-surface-dark/20 p-4">
                  <p className="font-bold text-dark text-sm mb-1">{c.competitor}</p>
                  <p className="text-sm text-dark-muted leading-relaxed mb-2">{c.summary}</p>
                  {c.notable_hooks && c.notable_hooks.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {c.notable_hooks.map((h, j) => (
                        <span key={j} className="px-2 py-0.5 rounded-full bg-surface text-dark-muted text-xs border border-surface-dark/30">
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                  {thumbs.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto">
                      {thumbs.map((a, j) => (
                        <img
                          key={j}
                          src={a.media_cached_url as string}
                          alt={a.headline || 'competitor ad'}
                          title={`${a.headline || ''}${a.longevity_days ? ` · running ${a.longevity_days}d` : ''}`}
                          className="h-28 w-28 object-cover rounded-lg border border-surface-dark/30 flex-shrink-0"
                          loading="lazy"
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Gap analysis — legacy. Same visibility rule. */}
      {!hideLegacyBrief && brief.gap_analysis && (
        <Section title="The opening" icon="ri-focus-3-line">
          <p className="text-sm text-dark leading-relaxed">{brief.gap_analysis}</p>
          {brief.differentiation_angles && brief.differentiation_angles.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {brief.differentiation_angles.map((a, i) => (
                <div key={i} className="rounded-lg bg-primary/5 border border-primary/15 p-3">
                  <p className="text-sm font-bold text-primary">{a.angle}</p>
                  <p className="text-xs text-dark-muted mt-0.5">{a.rationale}</p>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Product picker — legacy Phase 1 path. Hidden whenever brief.ideas
          exists (Phase 2 model — product selection happens upfront in the
          wizard, not on revisit). Locked-archived edge case still uses it. */}
      {!lockedArchived && (brief.ideas?.length ?? 0) === 0 && (
        !confirmedProduct ? (
          <Section title="Pick the product to feature" icon="ri-shopping-bag-3-line">
            <ProductPicker
              candidates={candidates}
              savedProducts={savedProducts}
              onConfirm={handleConfirm}
              busy={regenerating}
            />
            {regenError && (
              <p className="mt-2 text-xs text-red-600">Regen failed: {regenError}. Try again.</p>
            )}
          </Section>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-primary/25 bg-primary/5 p-3">
            <img src={confirmedProduct.imageUrl} alt={confirmedProduct.name || 'product'} className="w-12 h-12 object-cover rounded-lg border border-surface-dark/20 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-primary font-semibold">Featuring</p>
              <p className="text-sm font-bold text-dark truncate">{confirmedProduct.name || 'Your product'}</p>
            </div>
            <button onClick={() => setConfirmedProduct(null)} className="text-primary text-sm font-semibold hover:underline cursor-pointer flex-shrink-0">
              Change
            </button>
          </div>
        )
      )}

      {/* Phase 2 Winning Ideas — the new model. When brief.ideas is
          present, the 5×4 = 20 grid takes over as the primary surface.
          Each card has its own Generate button + the Generate-all CTA
          fires render-concept × N for all idle slots. Same component the
          wizard Step 6 uses, parent owns the state.
          ───────────────────────────────────────────────────────────── */}
      {brief.ideas && brief.ideas.length > 0 && (
        <Section title="Winning Ideas" icon="ri-magic-line">
          {orphanedRenderCount > 0 && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm flex items-center gap-2 text-amber-800">
              <i className="ri-information-line" />
              <span>
                <strong>{orphanedRenderCount} earlier render{orphanedRenderCount === 1 ? '' : 's'}</strong>
                {' '}from this analysis are saved in
                {' '}<a href="/gallery" className="font-bold underline">My Creatives</a>{' '}
                but couldn't be matched to specific slots below
                (from before today's update).
              </span>
            </div>
          )}
          {/* Top-right Generate-all bar. Only shows when there's work to do. */}
          {idleConceptCount > 0 && (
            <div className="mb-4 flex items-center justify-between rounded-xl bg-gradient-to-br from-primary/5 via-white to-primary-light/30 border border-primary/20 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-dark">
                  {idleConceptCount} concept{idleConceptCount === 1 ? '' : 's'} ready to render
                </p>
                <p className="text-xs text-dark-muted">
                  1 credit each — renders land in My Creatives + show inline below
                </p>
              </div>
              <button
                type="button"
                onClick={onOpenGenerateAll}
                disabled={generateAllBusy}
                className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {generateAllBusy ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Queueing…
                  </>
                ) : (
                  <>
                    <i className="ri-magic-line" />
                    Generate all {idleConceptCount} ({idleConceptCount}cr)
                  </>
                )}
              </button>
            </div>
          )}
          <WinningIdeasGrid
            ideas={brief.ideas}
            products={savedProducts}
            renderStateFor={renderStateFor}
            generateDisabled={generateDisabled}
            onGenerate={onGenerateConcept}
          />
          {/* Bottom-center sticky Generate-all bar — mirrors the top one
              so it stays in reach after scrolling through 5 angle sections. */}
          {idleConceptCount > 0 && (
            <div className="sticky bottom-4 mt-6 z-10 flex justify-center pointer-events-none">
              <div className="pointer-events-auto rounded-full bg-white border-2 border-primary/40 shadow-xl shadow-primary/20 px-2 py-1.5 flex items-center gap-2">
                <span className="hidden sm:inline text-xs text-dark-muted px-2">
                  {idleConceptCount} idle
                </span>
                <button
                  type="button"
                  onClick={onOpenGenerateAll}
                  disabled={generateAllBusy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {generateAllBusy ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Queueing…
                    </>
                  ) : (
                    <>
                      <i className="ri-magic-line" />
                      Generate all {idleConceptCount} ({idleConceptCount}cr)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* 5 concepts (TEXT) — legacy Phase 1 path. Shown only when there's
          NO Phase 2 brief.ideas (old jobs). New Phase 2 jobs surface the
          WinningIdeasGrid above instead. */}
      {(!brief.ideas || brief.ideas.length === 0) &&
       (lockedArchived || (confirmedProduct && brief.concepts && brief.concepts.length > 0)) &&
       brief.concepts && brief.concepts.length > 0 && (
        <Section title="5 concepts to beat them" icon="ri-lightbulb-flash-line">
          <ConceptSummaryGrid concepts={brief.concepts} />
        </Section>
      )}

      {/* Ads section — legacy Phase 1 paths. Phase 2 jobs surface ads
          inline on the WinningIdeasGrid cards (rendered thumbnail per
          card) so this fallback is only for old jobs without brief.ideas.
          - LIVE run: AiMagicCard grid (auto-render, Save buttons).
          - LOCKED archived run: SavedAdsGrid — read-only. */}
      {(!brief.ideas || brief.ideas.length === 0) && (lockedArchived ? (
        <Section title="Ads from this analysis" icon="ri-magic-line">
          <SavedAdsGrid posts={archivedSavedPosts} />
        </Section>
      ) : (
        confirmedProduct && brief.concepts && brief.concepts.length > 0 && (
          <Section title="Your ads" icon="ri-magic-line">
            <div className="grid md:grid-cols-2 gap-4">
              {brief.concepts.map((concept, i) => (
                <AiMagicCard
                  key={i}
                  concept={concept}
                  index={i}
                  product={confirmedProduct}
                  brandLogoUrl={brandLogoUrl}
                  jobId={jobId}
                />
              ))}
            </div>
          </Section>
        )
      ))}
    </div>
  );
}

function ConceptBadges({ concept, index }: { concept: AdConcept; index: number }) {
  const routeLabel = (concept.route && ROUTE_LABELS[concept.route]) || 'Single post';
  return (
    <div className="flex items-center justify-between mb-2">
      <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-semibold">
        {concept.angle || `Concept ${index + 1}`}
      </span>
      <span className="px-2 py-0.5 rounded-full bg-surface text-dark-muted text-xs border border-surface-dark/30">
        {routeLabel}
      </span>
    </div>
  );
}

// AI Magic card — auto-renders a full, baked, non-editable ad on mount.
//
// Single-post concepts: one gpt-image-1 call → one image.
// Carousel concepts: 4 SEQUENTIAL gpt-image-1 calls (one per frame, each
//   sharing the same product reference + shared_style). Frames stream in
//   one at a time so the user sees progress instead of waiting ~60-80s for
//   all 4 to land. Sequential also keeps us under the org's per-minute
//   input-images cap (5/min) — a parallel fan-out colliding with the other
//   4 concept cards was hitting 429s.
function AiMagicCard({ concept, index, product, brandLogoUrl, jobId }: { concept: AdConcept; index: number; product: ProductInfo; brandLogoUrl: string; jobId: string | null }) {
  const isCarousel = concept.route === 'carousel' && Array.isArray(concept.frames) && concept.frames.length > 0;
  const frameCount = isCarousel ? 4 : 1;

  // For carousel: a sparse array — urls[i] === null while frame i is still
  // rendering, becomes the url once it lands. For single: 1-element array.
  // status === 'done' when EVERY slot is filled.
  const [urls, setUrls] = useState<(string | null)[]>(() => Array(frameCount).fill(null));
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [zipStatus, setZipStatus] = useState<'idle' | 'zipping' | 'error'>('idle');
  // Save-to-My-Posts state. 'saved' is a terminal state for this concept —
  // the user can re-save by re-rendering (button stays disabled to avoid
  // duplicate rows).
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const firedRef = useRef(false);

  const run = useCallback(async () => {
    if (!product.imageUrl) return;
    setStatus('loading');
    setUrls(Array(frameCount).fill(null));

    const common = {
      mode: 'ai-magic' as const,
      productImageUrl: product.imageUrl,
      productName: product.name,
      productDescription: product.description,
      brandLogoUrl: brandLogoUrl || undefined,
      route: concept.route,
    };

    try {
      if (isCarousel) {
        // Fire 4 sequential calls — each backend call renders ONE frame and
        // returns its url. Sequential (not parallel) for rate-limit headroom
        // + so the user sees frames appear one at a time.
        for (let i = 0; i < 4; i++) {
          const res = await renderAdConcept({
            ...common,
            sharedStyle: concept.shared_style,
            frames: concept.frames,
            carouselFrameIndex: i,
          });
          const url = res.urls[0];
          if (!url) throw new Error(`Frame ${i + 1} returned no url`);
          setUrls((prev) => {
            const next = [...prev];
            next[i] = url;
            return next;
          });
        }
      } else {
        const res = await renderAdConcept({
          ...common,
          headline: concept.headline,
          subhead: concept.subhead,
          visualDirection: concept.visual_direction,
        });
        setUrls([res.urls[0] || null]);
      }
      setStatus('done');
    } catch (e) {
      console.error('[ad-strategist] AI Magic render failed', e);
      setStatus('error');
    }
  }, [product.imageUrl, product.name, product.description, concept.route, concept.shared_style, concept.frames, concept.headline, concept.subhead, concept.visual_direction, brandLogoUrl, isCarousel, frameCount]);

  useEffect(() => {
    if (firedRef.current || !product.imageUrl) return;
    firedRef.current = true;
    void run();
  }, [product.imageUrl, run]);

  // Carousel download — fetch each frame, ZIP via fflate, save as
  // carousel.zip. Sequential fetches are fine here (4 small PNGs from our
  // own Supabase Storage; total payload < 2MB). Guarded by allDone so it
  // only fires once every slot is populated.
  const allDone = urls.length === frameCount && urls.every((u): u is string => typeof u === 'string');
  const filledCount = urls.filter((u) => typeof u === 'string').length;

  const downloadCarousel = useCallback(async () => {
    if (!allDone) return;
    setZipStatus('zipping');
    try {
      const { zip } = await import('fflate');
      const entries: Record<string, Uint8Array> = {};
      for (let i = 0; i < urls.length; i++) {
        const u = urls[i];
        if (!u) throw new Error(`Frame ${i + 1} missing`);
        const r = await fetch(u);
        if (!r.ok) throw new Error(`Frame ${i + 1} fetch failed: ${r.status}`);
        const buf = new Uint8Array(await r.arrayBuffer());
        entries[`frame${i + 1}.png`] = buf;
      }
      const zipped: Uint8Array = await new Promise((resolve, reject) => {
        zip(entries, (err, data) => err ? reject(err) : resolve(data));
      });
      const blob = new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `carousel-concept-${index + 1}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      setZipStatus('idle');
    } catch (e) {
      console.error('[ad-strategist] carousel zip failed', e);
      setZipStatus('error');
    }
  }, [urls, index, allDone]);

  // Save to My Creatives. Single concepts save the rendered PNG; carousels save
  // the same 4-frame zip we'd download. Both paths share the existing
  // savePost helper (writes to Storage + posts table) with kind='ad'.
  const handleSave = useCallback(async () => {
    if (saveStatus === 'saving' || saveStatus === 'saved') return;
    if (urls.some((u) => !u)) return;
    setSaveStatus('saving');
    try {
      let blob: Blob;
      let contentType = 'image/png';
      let fileExt = 'png';
      let aspectRatio = '1:1';
      if (isCarousel && urls.length === 4) {
        const { zip } = await import('fflate');
        const entries: Record<string, Uint8Array> = {};
        for (let i = 0; i < urls.length; i++) {
          const u = urls[i]!;
          const r = await fetch(u);
          if (!r.ok) throw new Error(`Frame ${i + 1} fetch failed: ${r.status}`);
          entries[`frame${i + 1}.png`] = new Uint8Array(await r.arrayBuffer());
        }
        const zipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(entries, (err, data) => err ? reject(err) : resolve(data));
        });
        blob = new Blob([zipped.buffer as ArrayBuffer], { type: 'application/zip' });
        contentType = 'application/zip';
        fileExt = 'zip';
      } else {
        const r = await fetch(urls[0]!);
        if (!r.ok) throw new Error(`Image fetch failed: ${r.status}`);
        blob = await r.blob();
      }
      await savePost({
        blob,
        contentType,
        fileExt,
        aspectRatio,
        headline: concept.headline ?? null,
        tagline: concept.subhead ?? null,
        title: concept.angle || `Ad concept ${index + 1}`,
        sourceJobId: jobId ?? null,
      });
      setSaveStatus('saved');
    } catch (e) {
      console.error('[ad-strategist] save to My Creatives failed', e);
      setSaveStatus('error');
    }
  }, [saveStatus, urls, isCarousel, concept.headline, concept.subhead, concept.angle, index, jobId]);

  return (
    <div className="rounded-xl border border-surface-dark/25 bg-white p-4 flex flex-col">
      <div className="mb-3 relative w-full aspect-square rounded-lg border border-surface-dark/20 bg-surface overflow-hidden flex items-center justify-center">
        {status === 'error' ? (
          <button onClick={() => void run()} className="text-sm text-primary font-semibold cursor-pointer">
            <i className="ri-refresh-line" /> Render failed — retry
          </button>
        ) : !product.imageUrl ? (
          <p className="text-xs text-dark-muted text-center px-4">No product found on your website</p>
        ) : isCarousel ? (
          // 2x2 grid — placeholders render upfront, each tile fills in as
          // its frame's render completes. Hairline gap between tiles so the
          // user can tell at a glance "4 separate images, not one designed
          // 4-panel composition."
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-0.5 bg-surface-dark/20">
            {Array.from({ length: 4 }).map((_, i) => {
              const u = urls[i];
              return (
                <div key={i} className="relative bg-white overflow-hidden flex items-center justify-center">
                  {u ? (
                    <>
                      <img src={u} alt={`frame ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-semibold leading-none">
                        {i + 1}
                      </span>
                    </>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-dark-muted">
                      <i className="ri-loader-4-line text-lg animate-spin text-primary" />
                      <span className="text-[10px] leading-none">Frame {i + 1}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : status === 'done' && urls[0] ? (
          <img src={urls[0]} alt={concept.headline || 'ad'} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-dark-muted">
            <i className="ri-loader-4-line text-2xl animate-spin text-primary" />
            <span className="text-xs">Generating ad…</span>
          </div>
        )}
      </div>
      <ConceptBadges concept={concept} index={index} />
      {concept.why_it_wins && (
        <p className="text-xs text-dark-muted">
          <span className="font-semibold text-dark">Why it wins: </span>{concept.why_it_wins}
        </p>
      )}
      {isCarousel && status !== 'error' && (
        <p className="text-xs text-dark-muted mt-2">
          {allDone
            ? 'All 4 frames ready.'
            : `Streaming carousel frames… ${filledCount}/4`}
        </p>
      )}
      {/* Action row — Save + Download. Save writes to My Creatives (kind='ad')
          with a link back to the source job. Download is a raw file pull. */}
      {((isCarousel && allDone) || (!isCarousel && status === 'done' && urls[0])) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving' || saveStatus === 'saved'}
            className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer disabled:cursor-not-allowed ${
              saveStatus === 'saved'
                ? 'bg-emerald-100 text-emerald-700 cursor-default'
                : saveStatus === 'error'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-primary hover:bg-primary-hover text-white shadow-md shadow-primary/25 disabled:opacity-60'
            }`}
          >
            {saveStatus === 'saving' ? (
              <><i className="ri-loader-4-line animate-spin" /> Saving…</>
            ) : saveStatus === 'saved' ? (
              <><i className="ri-check-line" /> Saved to My Creatives</>
            ) : saveStatus === 'error' ? (
              <><i className="ri-error-warning-line" /> Retry save</>
            ) : (
              <><i className="ri-save-line" /> Save to My Creatives</>
            )}
          </button>
          {isCarousel ? (
            <button
              onClick={downloadCarousel}
              disabled={zipStatus === 'zipping'}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-white border border-dark-muted/30 hover:border-primary hover:text-primary disabled:opacity-60 disabled:cursor-not-allowed text-dark-muted text-sm font-bold transition-all cursor-pointer"
            >
              {zipStatus === 'zipping' ? (
                <><i className="ri-loader-4-line animate-spin" /> Bundling…</>
              ) : zipStatus === 'error' ? (
                <><i className="ri-error-warning-line" /> Retry download</>
              ) : (
                <><i className="ri-download-2-line" /> Download .zip</>
              )}
            </button>
          ) : (
            <a
              href={urls[0]!}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-white border border-dark-muted/30 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
            >
              <i className="ri-download-2-line" /> Download
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// formatPlatformBadge — normalize the Meta surface list to a compact chip
// label. Drops MESSENGER + AUDIENCE_NETWORK from the user-visible label
// (those are minor delivery surfaces; FB+IG carry the signal). Returns
// null when the vendor didn't classify the ad — the thumbnail then renders
// without a chip (better than a confusing empty pill).
function formatPlatformBadge(
  platforms: string[] | undefined,
): { label: string; bg: string; title: string } | null {
  if (!platforms || platforms.length === 0) return null;
  const upper = platforms.map((p) => p.toUpperCase());
  const fb = upper.includes('FACEBOOK');
  const ig = upper.includes('INSTAGRAM');
  if (fb && ig) {
    return {
      label: 'FB · IG',
      // Gradient signals "both surfaces" — FB blue → IG pink-purple.
      bg: 'bg-gradient-to-r from-[#1877F2] to-[#E1306C]',
      title: 'Runs on both Facebook and Instagram',
    };
  }
  if (ig) {
    return {
      label: 'IG',
      bg: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
      title: 'Instagram',
    };
  }
  if (fb) {
    return {
      label: 'FB',
      bg: 'bg-[#1877F2]',
      title: 'Facebook',
    };
  }
  return null;
}

// AdMetaChips — at-a-glance signal under each competitor thumbnail. Only
// two chips here: ad format (image/video/carousel) and longevity (how many
// days the ad has been running, the strongest "is this working" signal).
// The CTA pill was intentionally REMOVED from this list — it's noise at
// tile size and stays in the CompetitorAdModal where there's room.
// Chips don't render when the vendor didn't return that field; row hides
// entirely for thin records.
function AdMetaChips({ ad }: { ad: GatheredAd }) {
  const showFormat = ad.format && ad.format !== 'unknown';
  const showLongevity = ad.longevity_days != null && ad.longevity_days > 0;
  if (!showFormat && !showLongevity) return null;
  const formatIcon = ad.format === 'video'
    ? 'ri-video-line'
    : ad.format === 'carousel'
      ? 'ri-stack-line'
      : 'ri-image-line';
  return (
    <div className="px-1.5 py-1 flex flex-wrap items-center gap-1">
      {showFormat && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-surface text-dark-muted text-[9px] font-bold border border-surface-dark/30 leading-none"
          title={`Format: ${ad.format}`}
        >
          <i className={`${formatIcon} text-[10px]`} />
          {ad.format}
        </span>
      )}
      {showLongevity && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold border border-emerald-100 leading-none"
          title={`Running ${ad.longevity_days} days — long-running = working`}
        >
          <i className="ri-timer-line text-[10px]" />
          {ad.longevity_days}d
        </span>
      )}
    </div>
  );
}

// CompetitorAdsPreview — what shows inside Step 3 of the stepper once the
// Apify scrape returns. Compact per-competitor block with their first few
// cached ad thumbnails. Click a thumbnail → opens CompetitorAdModal with
// the full ad detail (text, headline, CTA, how long it's been running,
// platforms, link to the original on the Meta Ad Library). Skip blocks
// with no ads (vendor returned nothing).
function CompetitorAdsPreview({ gathered }: { gathered: AdStrategistResult['gathered'] }) {
  const [openAd, setOpenAd] = useState<{ ad: GatheredAd; advertiser: string } | null>(null);
  const blocks = gathered.competitors.filter((b) => b.ads.length > 0);
  if (blocks.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-6 text-center">
        <p className="text-sm font-semibold text-dark">No competitor ads found</p>
        <p className="text-xs text-dark-muted mt-0.5">
          Try a different Facebook page URL — the vendor returned nothing for these.
        </p>
      </div>
    );
  }
  return (
    <>
      <div className="space-y-3">
        {blocks.map((b, i) => (
          <AdvertiserBlock
            key={i}
            label={b.label}
            ads={b.ads}
            onOpenAd={(ad) => setOpenAd({ ad, advertiser: b.label })}
          />
        ))}
      </div>
      {openAd && (
        <CompetitorAdModal
          ad={openAd.ad}
          advertiser={openAd.advertiser}
          onClose={() => setOpenAd(null)}
        />
      )}
    </>
  );
}

// AdvertiserBlock — one card per competitor inside Step 3. Renders the
// thumbnails as a HORIZONTAL SCROLL STRIP (not a wrapping grid) with
// prev/next arrow buttons. Fixed tile width so the scroll-by-one-tile
// gesture is predictable. Arrows fade in/out based on how much room
// there is to scroll in each direction (no point in a "next" button when
// you're already at the end).
function AdvertiserBlock({
  label,
  ads,
  onOpenAd,
}: {
  label: string;
  ads: GatheredAd[];
  onOpenAd: (ad: GatheredAd) => void;
}) {
  const thumbs = ads.filter((a) => a.media_cached_url);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Re-check arrow visibility on scroll + on resize. 8px slack on each
  // side so the arrows hide cleanly at the ends instead of lingering with
  // 1-2px residue.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 8);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [thumbs.length]);

  // Tile width in px — kept in sync with the Tailwind w-32 class on each
  // tile below so the scroll-by-one-tile maths is consistent.
  const TILE_W = 128;
  const TILE_GAP = 8; // matches gap-2 (8px)
  const scrollByOne = (dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: dir * (TILE_W + TILE_GAP), behavior: 'smooth' });
  };

  return (
    <div className="rounded-xl border border-surface-dark/20 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="font-bold text-dark text-sm">{label}</p>
        <span className="text-xs text-dark-muted">
          {/* Count the THUMBNAILS shown, not records pulled — backend keeps
              text metadata for more ads than it caches thumbnails for (the
              extras still inform the LLM brief, but aren't visible here),
              so showing pulled-count would mismatch the strip. */}
          {thumbs.length} ad{thumbs.length === 1 ? '' : 's'}
        </span>
      </div>
      {thumbs.length > 0 ? (
        // Position-relative wrapper so the arrow buttons can sit over the
        // edges of the scroll strip. The scroll strip itself is the only
        // overflow-x-auto element — arrows live above it.
        <div className="relative">
          {/* Prev arrow */}
          {canLeft && (
            <button
              type="button"
              onClick={() => scrollByOne(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/95 border border-surface-dark/40 text-dark hover:text-primary hover:border-primary shadow-md shadow-black/10 flex items-center justify-center cursor-pointer transition-colors"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
          )}
          {/* Next arrow */}
          {canRight && (
            <button
              type="button"
              onClick={() => scrollByOne(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/95 border border-surface-dark/40 text-dark hover:text-primary hover:border-primary shadow-md shadow-black/10 flex items-center justify-center cursor-pointer transition-colors"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          )}
          {/* Horizontal scroll strip — snap-x so tiles land cleanly when
              scrolled. scrollbar-hidden via a Tailwind utility (added in
              index.css). */}
          <div
            ref={scrollRef}
            className="flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-1 -mb-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          >
            {thumbs.map((a, j) => {
              const pBadge = formatPlatformBadge(a.platforms);
              return (
                <button
                  type="button"
                  key={j}
                  onClick={() => onOpenAd(a)}
                  className="group flex-shrink-0 w-32 snap-start relative flex flex-col rounded-lg overflow-hidden bg-white border border-surface-dark/25 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-primary/15 hover:border-primary/30 text-left"
                  title={a.ad_text || a.headline || 'View ad detail'}
                >
                  <div className="relative aspect-square bg-surface-dark/20">
                    <img
                      src={a.media_cached_url || ''}
                      alt={a.headline || `ad ${j + 1}`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {pBadge && (
                      <span
                        className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold leading-none text-white ${pBadge.bg}`}
                        title={pBadge.title}
                      >
                        {pBadge.label}
                      </span>
                    )}
                    <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-zoom-in-line text-xs" />
                    </span>
                  </div>
                  <AdMetaChips ad={a} />
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-xs text-dark-muted">
          No images available for this advertiser.
        </p>
      )}
    </div>
  );
}

// CompetitorAdModal — fullscreen-ish detail view of one competitor ad.
// Renders the cached creative big + every metadata field we have (ad text,
// headline, link description, CTA, format, longevity, platforms) + outbound
// links to Meta Ad Library (so the user can see the ORIGINAL ad with video
// playback, all carousel frames, etc.) and the advertiser's landing page.
//
// Close via backdrop click, Escape key, or the explicit ✕ button. Keeps
// scroll locked on body while open. Matches the appv2 glass family
// (rounded-2xl + glass-stroke + white-tinted shadow).
function CompetitorAdModal({
  ad,
  advertiser,
  onClose,
}: {
  ad: GatheredAd;
  advertiser: string;
  onClose: () => void;
}) {
  // Lock body scroll + Escape-to-close.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Deep-link to the original ad on the Meta Ad Library. Works whenever we
  // captured ad_archive_id from the vendor (which is the common case).
  const adLibraryUrl = ad.ad_archive_id
    ? `https://www.facebook.com/ads/library/?id=${encodeURIComponent(ad.ad_archive_id)}`
    : null;

  const pBadge = formatPlatformBadge(ad.platforms);

  // Format date pretty for first/last shown timestamps.
  const fmtDate = (iso: string | undefined) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Ad from ${advertiser}`}
    >
      <div
        // Narrower max-width (3xl → 2xl) + slightly less max-height than
        // viewport, so the modal feels framed rather than edge-to-edge.
        // overflow-y-auto on the outer scrolls the WHOLE modal as one
        // unit — close + header stay reachable from the top.
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl glass-stroke bg-white shadow-2xl shadow-primary/15"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-surface-dark/30 text-dark-muted hover:text-dark hover:bg-white shadow-sm flex items-center justify-center cursor-pointer transition-colors"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Header — advertiser + platform badge */}
        <div className="px-6 pt-5 pb-3 border-b border-surface-dark/20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-sword-line text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-dark-muted uppercase tracking-wide">Competitor ad</p>
              <h3 className="font-display font-bold text-dark text-base truncate">{advertiser}</h3>
            </div>
            {pBadge && (
              <span
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-bold text-white ${pBadge.bg}`}
                title={pBadge.title}
              >
                {pBadge.label}
              </span>
            )}
          </div>
        </div>

        {/* Body — split: image on the left, text on the right (stacked on
            narrow widths so it stays readable on mobile). */}
        <div className="grid md:grid-cols-[1fr_1fr] gap-0 md:gap-0">
          {/* Image pane — image capped so the right-side detail pane
              isn't pushed off-screen on common laptop viewports. */}
          <div className="bg-surface-dark/10 p-3 md:p-4 flex items-center justify-center">
            {ad.media_cached_url ? (
              <img
                src={ad.media_cached_url}
                alt={ad.headline || 'Ad creative'}
                className="max-w-full max-h-[42vh] object-contain rounded-lg shadow-md shadow-black/10"
              />
            ) : (
              <div className="py-12 text-center text-dark-muted text-sm">
                <i className="ri-image-line text-3xl" />
                <p className="mt-2">No image cached for this ad</p>
              </div>
            )}
          </div>

          {/* Detail pane */}
          <div className="p-5 md:p-6 space-y-4">
            {/* Headline */}
            {ad.headline && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Headline</p>
                <p className="font-display font-bold text-dark text-base leading-snug">{ad.headline}</p>
              </div>
            )}

            {/* Ad body text */}
            {ad.ad_text && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Ad copy</p>
                <p className="text-sm text-dark leading-relaxed whitespace-pre-wrap">{ad.ad_text}</p>
              </div>
            )}

            {/* Link description (sub-line under the headline in Meta's UI) */}
            {ad.link_desc && (
              <div>
                <p className="text-[10px] font-bold text-dark-muted uppercase tracking-wide mb-1">Link description</p>
                <p className="text-sm text-dark-muted leading-relaxed">{ad.link_desc}</p>
              </div>
            )}

            {/* CTA + format + how long it's been running — small chip row */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {ad.cta && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                  <i className="ri-cursor-line text-xs" /> {ad.cta}
                </span>
              )}
              {ad.format && ad.format !== 'unknown' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-dark-muted text-xs font-semibold border border-surface-dark/30">
                  <i className={`text-xs ${ad.format === 'video' ? 'ri-video-line' : ad.format === 'carousel' ? 'ri-stack-line' : 'ri-image-line'}`} /> {ad.format}
                </span>
              )}
              {ad.longevity_days != null && ad.longevity_days > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100"
                  title="Longevity = how many days this ad has been running. Long-running ads usually mean 'it's working.'"
                >
                  <i className="ri-timer-line text-xs" /> Running {ad.longevity_days}d
                </span>
              )}
              {ad.variations != null && ad.variations > 1 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface text-dark-muted text-xs font-semibold border border-surface-dark/30">
                  <i className="ri-file-copy-line text-xs" /> {ad.variations} variations
                </span>
              )}
            </div>

            {/* Optional: first/last shown timestamps. Small text, dim. */}
            {(ad.first_shown || ad.last_shown) && (
              <p className="text-[11px] text-dark-muted pt-1">
                {fmtDate(ad.first_shown) && <>First shown: {fmtDate(ad.first_shown)}</>}
                {fmtDate(ad.first_shown) && fmtDate(ad.last_shown) && <> · </>}
                {fmtDate(ad.last_shown) && <>Last shown: {fmtDate(ad.last_shown)}</>}
              </p>
            )}

            {/* External links — View on Meta + Visit landing. */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-surface-dark/20">
              {adLibraryUrl && (
                <a
                  href={adLibraryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line" /> View on Meta Ad Library
                </a>
              )}
              {ad.landing_url && (
                <a
                  href={ad.landing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-xs font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-link" /> Visit landing page
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// OpeningsPreview — what shows inside Step 4 of the stepper. Brand snapshot
// + gap analysis + differentiation angles. Same content the user used to
// see in ResultsView's first three sections — moving it here lets the
// stepper own the "show me what you found" narrative end-to-end. ResultsView
// hides these sections on stepper-handoff (briefShownInStepper={true}) so
// they don't appear twice.
// ─────────────────────────────────────────────────────────────────────────
// WinningIdeasGrid — Phase 2 output, 5 ideas × 4 distinct concepts each.
//
// v2 (this pass): each card uses the product image as a HERO + one of 3
// stylised templates picked by hook_type. Reads more like a creative pitch
// deck than the prior text-only list.
//
//   Template A — "Hero image" (default): product image dominates, headline
//     reads as an ad headline overlaid below. Used for demonstration,
//     lifestyle, ingredient-focus, comparison, ritual, behind-scenes,
//     before-after, social-proof.
//   Template B — "Big statement": headline becomes the visual, product as
//     small accent in the corner. Used for statistic, problem-agitation.
//   Template C — "Quote": speech-bubble framing, headline rendered as a
//     customer/founder quote. Used for testimonial, founder-story.
//
// Visual direction text moves to a small "creative direction" footer.
// Product swap UI lands in a follow-up.
// ─────────────────────────────────────────────────────────────────────────

// Hook → palette. Used by all 3 templates for chip + accent tints.
type HookPalette = {
  /** Tailwind classes for the hook chip (bg + text). */
  chip: string;
  /** Accent border tint for the card. */
  accent: string;
  /** Hex string used for inline gradients on Template B/C. */
  hex: string;
};

const HOOK_PALETTE: Record<string, HookPalette> = {
  'demonstration':     { chip: 'bg-sky-100 text-sky-800',         accent: 'border-sky-200',     hex: '#0284c7' },
  'before-after':      { chip: 'bg-emerald-100 text-emerald-800', accent: 'border-emerald-200', hex: '#059669' },
  'social-proof':      { chip: 'bg-amber-100 text-amber-800',     accent: 'border-amber-200',   hex: '#d97706' },
  'testimonial':       { chip: 'bg-rose-100 text-rose-800',       accent: 'border-rose-200',    hex: '#e11d48' },
  'comparison':        { chip: 'bg-violet-100 text-violet-800',   accent: 'border-violet-200',  hex: '#7c3aed' },
  'lifestyle':         { chip: 'bg-orange-100 text-orange-800',   accent: 'border-orange-200',  hex: '#ea580c' },
  'ingredient-focus':  { chip: 'bg-lime-100 text-lime-800',       accent: 'border-lime-200',    hex: '#65a30d' },
  'problem-agitation': { chip: 'bg-red-100 text-red-800',         accent: 'border-red-200',     hex: '#dc2626' },
  'founder-story':     { chip: 'bg-indigo-100 text-indigo-800',   accent: 'border-indigo-200',  hex: '#4f46e5' },
  'ritual':            { chip: 'bg-pink-100 text-pink-800',       accent: 'border-pink-200',    hex: '#db2777' },
  'behind-scenes':     { chip: 'bg-slate-100 text-slate-800',     accent: 'border-slate-200',   hex: '#475569' },
  'statistic':         { chip: 'bg-cyan-100 text-cyan-800',       accent: 'border-cyan-200',    hex: '#0891b2' },
};

const DEFAULT_PALETTE: HookPalette = {
  chip: 'bg-surface-dark/30 text-dark-muted',
  accent: 'border-surface-dark/40',
  hex: '#64748b',
};

const hookPalette = (hook: string): HookPalette => HOOK_PALETTE[hook] ?? DEFAULT_PALETTE;


// Per-card render state from parent. Card looks the same in all states
// but the gradient hero overlays a different element:
//   • idle    — Generate button (1 credit) centered over hook pill
//   • pending — small spinner over hook pill (user just clicked, awaiting backend ack)
//   • queued  — full-overlay loader + "Rendering…" text
//   • rendered — thumbnail of the rendered post, click → detail page
type CardRenderState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'queued'; postId: string }
  | { status: 'rendered'; postId: string; imageUrl: string | null };

interface ConceptCardProps {
  concept: AdStrategist20Concept;
  product: UserProduct | undefined;
  ideaIdx: number;
  conceptIdx: number;
  renderState: CardRenderState;
  /** Disabled while Generate-all is bursting all concepts in parallel. */
  generateDisabled: boolean;
  onGenerate: (ideaIdx: number, conceptIdx: number) => void;
}

// Single unified concept card — fits 4-up in a row at xl+ so each idea's
// 4 distinct concepts read as one horizontal strip.
//
// v3 (per-card Generate): the gradient hero now ALSO carries the action
// for this slot. Idle → Generate button. Pending/queued → loader. Rendered
// → the rendered thumbnail (click → MyCreatives detail page). The hook
// pill stays in all states as the at-a-glance discipline cue.
function ConceptCard({
  concept, product, ideaIdx, conceptIdx,
  renderState, generateDisabled, onGenerate,
}: ConceptCardProps) {
  const palette = hookPalette(concept.hook_type);

  // Statistic hook: try to pull a leading number/percent so the gradient
  // hero shows the stat as the visual element. Falls back to no big number.
  const statMatch = concept.hook_type === 'statistic'
    ? concept.headline.match(/^(\d+%?|\d+x|\d+\.\d+)\b\s*/i)
    : null;
  const bigStat = statMatch ? statMatch[1] : null;

  const showRenderedThumb = renderState.status === 'rendered' && renderState.imageUrl;

  return (
    <div className={`group flex flex-col rounded-2xl bg-white border ${palette.accent} overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
      {/* Hero zone — locked 1:1 so cards line up regardless of the rendered
          ad's native aspect (1:1 / 4:5 / 9:16 / etc.). Rendered image uses
          object-contain so non-1:1 ads show letterboxed on a neutral
          backdrop instead of being cropped. */}
      <div
        className="relative aspect-square flex items-center justify-center px-3 py-5"
        style={{
          background: showRenderedThumb
            ? '#f5f5fa'
            : `linear-gradient(135deg, ${palette.hex}18 0%, ${palette.hex}05 100%)`,
        }}
      >
        {showRenderedThumb && (
          // Rendered → show the actual ad. Click → detail page so user
          // can publish / reimagine / etc. Replaces the gradient bg entirely.
          <a
            href={`/creatives/${renderState.postId}`}
            className="absolute inset-0 block group/thumb"
            title="Open in My Creatives →"
          >
            <img
              src={renderState.imageUrl!}
              alt={concept.headline}
              className="w-full h-full object-contain"
              loading="lazy"
            />
            {/* Hover overlay reveals the "view" affordance */}
            <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100">
              <span className="px-3 py-1.5 rounded-full bg-white text-dark text-xs font-bold inline-flex items-center gap-1.5">
                <i className="ri-arrow-right-line" />
                Open in My Creatives
              </span>
            </div>
            {/* Hook pill stays — pinned bottom-left, less noisy than centered. */}
            <span className={`${palette.chip} absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm`}>
              <i className="ri-flashlight-line text-[10px]" />
              {concept.hook_type.replace(/-/g, ' ')}
            </span>
          </a>
        )}
        {!showRenderedThumb && (
          // Idle / pending / queued — stack: hook pill + bigStat (optional)
          // + Generate button or loader.
          <div className="flex flex-col items-center gap-2 w-full">
            <span className={`${palette.chip} inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide`}>
              <i className="ri-flashlight-line text-[10px]" />
              {concept.hook_type.replace(/-/g, ' ')}
            </span>
            {bigStat && (
              <div
                className="font-display font-black tracking-tight leading-none text-3xl"
                style={{ color: palette.hex }}
              >
                {bigStat}
              </div>
            )}
            {renderState.status === 'idle' && (
              <button
                type="button"
                onClick={() => onGenerate(ideaIdx, conceptIdx)}
                disabled={generateDisabled}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-sparkling-2-line" />
                Generate <span className="opacity-70">(1cr)</span>
              </button>
            )}
            {(renderState.status === 'pending' || renderState.status === 'queued') && (
              <div className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-dark/30 text-dark-muted text-xs font-bold">
                <i className="ri-loader-4-line animate-spin" />
                {renderState.status === 'pending' ? 'Queueing…' : 'Rendering…'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Copy block */}
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <h4 className="font-display font-bold text-dark text-sm leading-snug">
          {concept.headline}
        </h4>
        {concept.subhead && (
          <p className="text-xs text-dark-muted leading-snug">
            {concept.subhead}
          </p>
        )}
        {concept.visual_direction && (
          <p className="text-[10px] text-dark-muted/70 italic leading-snug pt-1 mt-1 border-t border-surface-dark/20">
            <i className="ri-camera-line text-dark-muted/60 mr-1" />
            {concept.visual_direction}
          </p>
        )}
        {/* Product footer — 16px thumb (2× prior). */}
        <div className="mt-auto pt-2 border-t border-surface-dark/20 flex items-center gap-2">
          {product?.imageUrl && (
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface/40 flex-shrink-0 border border-surface-dark/30">
              <img
                src={product.imageUrl}
                alt=""
                className="w-full h-full object-contain p-1"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-dark-muted/70 uppercase tracking-wide">
              Product
            </p>
            <p className="text-xs font-bold text-dark line-clamp-2 leading-tight">
              {product?.name || '(unknown product)'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function WinningIdeasGrid({
  ideas,
  products,
  renderStateFor,
  generateDisabled,
  onGenerate,
}: {
  ideas: AdStrategist20Idea[];
  products: UserProduct[];
  /** Lookup the per-card state from parent. Pure function so the grid stays
   *  uncoupled from the parent's Map representation. */
  renderStateFor: (ideaIdx: number, conceptIdx: number) => CardRenderState;
  generateDisabled: boolean;
  onGenerate: (ideaIdx: number, conceptIdx: number) => void;
}) {
  const productById = new Map(products.map((p) => [p.id, p]));

  return (
    <div className="space-y-8">
      {ideas.map((idea, ideaIdx) => (
        <div key={ideaIdx} className="space-y-4">
          {/* Idea section header — larger + bolder than before so each
              strategic angle reads as its own chapter. */}
          <div className="flex items-baseline gap-3 pb-2 border-b border-surface-dark/30">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold flex-shrink-0">
              {ideaIdx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Strategic angle
              </p>
              <h3 className="font-display font-bold text-dark text-lg leading-tight">
                {idea.angle_name || `Angle ${idea.angle_idx + 1}`}
              </h3>
            </div>
          </div>

          {/* 4 concept cards per row */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {idea.concepts.map((c, conceptIdx) => (
              <ConceptCard
                key={conceptIdx}
                concept={c}
                product={productById.get(c.recommended_product_id)}
                ideaIdx={ideaIdx}
                conceptIdx={conceptIdx}
                renderState={renderStateFor(ideaIdx, conceptIdx)}
                generateDisabled={generateDisabled}
                onGenerate={onGenerate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function OpeningsPreview({ brief }: { brief: AdBrief }) {
  return (
    <div className="space-y-4">
      {brief.brand_snapshot && (
        <div>
          <p className="text-xs font-bold text-dark-muted uppercase tracking-wide mb-1">
            Your brand right now
          </p>
          <p className="text-sm text-dark leading-relaxed">{brief.brand_snapshot}</p>
        </div>
      )}
      {brief.gap_analysis && (
        <div>
          <p className="text-xs font-bold text-dark-muted uppercase tracking-wide mb-1">
            Where the competition isn't showing up
          </p>
          <p className="text-sm text-dark leading-relaxed">{brief.gap_analysis}</p>
        </div>
      )}
      {brief.differentiation_angles && brief.differentiation_angles.length > 0 && (
        <div>
          <p className="text-xs font-bold text-dark-muted uppercase tracking-wide mb-2">
            Angles you can own
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {brief.differentiation_angles.map((a, i) => (
              <div key={i} className="rounded-lg bg-primary/5 border border-primary/15 p-3">
                <p className="text-sm font-bold text-primary">{a.angle}</p>
                <p className="text-xs text-dark-muted mt-0.5">{a.rationale}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ConceptsLoadingSkeleton — shown inside Step 6 while the LLM is writing
// the 5 concepts (regenerating=true). 5 placeholder cards laid out the
// same way as the real AiMagicCard grid, with pulsing image + headline +
// meta lines. Soft "writing your ads" copy at the top so the user knows
// what's happening (this stage takes ~5-15s before the image renders
// start). Once regenerating flips false + concepts arrive, the real
// cards take over.
function ConceptsLoadingSkeleton() {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-dark-muted text-sm">
        <i className="ri-loader-4-line text-primary animate-spin" />
        <span><strong className="text-dark font-bold">Writing your 5 ad concepts…</strong> 5-15s. Image rendering starts right after.</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-surface-dark/25 bg-white p-4 flex flex-col gap-3"
          >
            {/* Placeholder image tile */}
            <div className="relative w-full aspect-square rounded-lg bg-surface-dark/30 animate-pulse" />
            {/* Placeholder badges row */}
            <div className="flex items-center justify-between gap-2">
              <div className="h-4 w-20 rounded-full bg-surface-dark/40 animate-pulse" />
              <div className="h-4 w-16 rounded-full bg-surface-dark/30 animate-pulse" />
            </div>
            {/* Placeholder why-it-wins line */}
            <div className="space-y-1.5">
              <div className="h-2.5 w-full rounded bg-surface-dark/30 animate-pulse" />
              <div className="h-2.5 w-4/5 rounded bg-surface-dark/30 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// SavedAdsGrid — read-only thumbnails of whatever the user kept in My Creatives
// for a given analysis (filtered by source_job_id at the parent level).
// Used only on locked archived analyses: we don't re-render ads when re-
// opening a past run, we just show what was saved. Carousel saves are
// .zip; we render a stacked-frames placeholder for those (downloading
// them lives in My Creatives, not here).
function SavedAdsGrid({ posts }: { posts: Post[] }) {
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        posts.map(async (p) => {
          if (p.image_path.endsWith('.zip')) return; // carousel — no preview
          try {
            const url = await getSignedImageUrl(p.image_path, 3600);
            if (!cancelled) next[p.id] = url;
          } catch { /* placeholder fallback */ }
        }),
      );
      if (!cancelled) setThumbUrls(next);
    })();
    return () => { cancelled = true; };
    // Re-resolve when the underlying list changes (rare on this page).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.map((p) => p.id).join('|')]);

  if (posts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-8 text-center">
        <i className="ri-bookmark-line text-2xl text-dark-muted/60" />
        <p className="text-sm font-semibold text-dark mt-2">No ads saved from this analysis</p>
        <p className="text-xs text-dark-muted mt-0.5">
          The next time you generate ads, hit <em className="not-italic font-semibold">Save to My Creatives</em> on the ones you want to keep.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {posts.map((p) => {
        const isZip = p.image_path.endsWith('.zip');
        const url = thumbUrls[p.id];
        return (
          <div
            key={p.id}
            className="rounded-xl glass-stroke bg-white/85 overflow-hidden shadow-sm shadow-primary/5"
            title={p.title || p.headline || 'Saved ad'}
          >
            <div className="relative aspect-square bg-surface-dark/20">
              {isZip ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-dark-muted bg-gradient-to-br from-primary/5 to-accent/5">
                  <i className="ri-stack-line text-2xl" />
                  <span className="text-[10px] font-bold mt-1">Carousel (4 frames)</span>
                </div>
              ) : url ? (
                <img src={url} alt={p.title || 'Saved ad'} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 animate-pulse bg-surface-dark/30" />
              )}
            </div>
            {(p.headline || p.title) && (
              <div className="px-3 py-2">
                <p className="text-xs font-bold text-dark truncate" title={p.headline || p.title || ''}>
                  {p.headline || p.title}
                </p>
                {p.tagline && (
                  <p className="text-[11px] text-dark-muted truncate mt-0.5" title={p.tagline}>
                    {p.tagline}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// 5 concepts to beat them — text-only summary cards shown BEFORE the
// product picker. Angle + headline + why-it-wins; no images.
function ConceptSummaryGrid({ concepts }: { concepts: AdConcept[] }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {concepts.map((c, i) => (
        <div key={i} className="rounded-xl border border-surface-dark/20 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-semibold">
              {c.angle || `Concept ${i + 1}`}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-surface text-dark-muted text-xs border border-surface-dark/30">
              {(c.route && ROUTE_LABELS[c.route]) || 'Single post'}
            </span>
          </div>
          <p className="font-display font-bold text-dark text-base leading-snug">{c.headline}</p>
          {c.subhead && <p className="text-sm text-dark-muted mt-0.5">{c.subhead}</p>}
          {c.why_it_wins && (
            <p className="text-xs text-dark-muted mt-2">
              <span className="font-semibold text-dark">Why it wins: </span>{c.why_it_wins}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// Hybrid product picker — auto-detected candidates from the site (best-seller
// first), a "paste a product URL" path that works on any site (reuses the
// /api/ad-strategist/scrape-product endpoint), and My Products as a third
// source. User picks one + clicks Generate; renders fire below.
interface PickerOption {
  key: string;
  imageUrl: string;
  name: string;
  description: string;
  source: 'website' | 'library';
}

function ProductPicker({
  candidates,
  savedProducts,
  onConfirm,
  busy = false,
}: {
  candidates: ProductCandidate[];
  savedProducts: UserProduct[];
  onConfirm: (p: ProductInfo) => void;
  busy?: boolean;
}) {
  const candidateOptions: PickerOption[] = candidates.map((c, i) => ({
    key: `cand-${i}`,
    imageUrl: c.image_url,
    name: c.name || `Product ${i + 1}`,
    description: c.description || '',
    source: 'website',
  }));
  const libraryOptions: PickerOption[] = savedProducts.map((p) => ({
    key: `lib-${p.id}`,
    imageUrl: p.imageUrl || '',
    name: p.name || 'Saved product',
    description: p.description || '',
    source: 'library',
  }));

  const [tab, setTab] = useState<'store' | 'library'>(
    candidateOptions.length > 0 || libraryOptions.length === 0 ? 'store' : 'library',
  );
  const [pasted, setPasted] = useState<PickerOption[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(
    candidateOptions[0]?.key ?? null,
  );
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);

  const storeOptions = [...pasted, ...candidateOptions];
  const allOptions = [...storeOptions, ...libraryOptions];
  const selected = allOptions.find((o) => o.key === selectedKey) || null;

  const handlePaste = async () => {
    const url = pasteUrl.trim();
    if (!url) return;
    setPasteLoading(true);
    setPasteError(null);
    try {
      const c = await scrapeAdStrategistProduct(url);
      const key = `paste-${Date.now()}`;
      const opt: PickerOption = {
        key,
        imageUrl: c.image_url,
        name: c.name || 'Pasted product',
        description: c.description || '',
        source: 'website',
      };
      setPasted((prev) => [opt, ...prev]);
      setSelectedKey(key);
      setPasteUrl('');
    } catch (err) {
      setPasteError(err instanceof Error ? err.message : 'Could not load that product page.');
    } finally {
      setPasteLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex gap-2">
        <PickerTab active={tab === 'store'} onClick={() => setTab('store')} icon="ri-store-2-line" label={`Store best sellers${storeOptions.length ? ` (${storeOptions.length})` : ''}`} />
        <PickerTab active={tab === 'library'} onClick={() => setTab('library')} icon="ri-shopping-bag-3-line" label={`From My Products${libraryOptions.length ? ` (${libraryOptions.length})` : ''}`} />
      </div>

      {/* Paste URL row — only on Store tab (used to add a product when the
          auto-detect missed it, or override). Stays above the scroll. */}
      {tab === 'store' && (
        <div>
          <div className="flex gap-2">
            <input
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !pasteLoading) handlePaste(); }}
              placeholder="Or paste a product URL — yourbrand.com/products/hero (works on any site)"
              className="flex-1 px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
            />
            <button
              onClick={handlePaste}
              disabled={!pasteUrl.trim() || pasteLoading}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-primary/40 text-primary text-sm font-bold hover:bg-primary/5 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pasteLoading ? (<><i className="ri-loader-4-line animate-spin" /> Loading</>) : (<><i className="ri-add-line" /> Add</>)}
            </button>
          </div>
          {pasteError && <p className="text-xs text-red-600 mt-1">{pasteError}</p>}
        </div>
      )}

      {/* Scrollable grid — fixed height so only ~2 rows are visible; everything
          else scrolls. Generate button below stays put. */}
      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-surface-dark/20 bg-surface/40 p-2">
        {tab === 'store' ? (
          storeOptions.length > 0 ? (
            <ProductGrid options={storeOptions} selectedKey={selectedKey} onSelect={setSelectedKey} />
          ) : (
            <div className="py-10 text-center">
              <p className="text-sm font-semibold text-dark">No products found from your site.</p>
              <p className="text-xs text-dark-muted mt-1">
                It may be JS-rendered or bot-protected (e.g. Nike). Paste a product URL above to add one.
              </p>
            </div>
          )
        ) : libraryOptions.length > 0 ? (
          <ProductGrid options={libraryOptions} selectedKey={selectedKey} onSelect={setSelectedKey} />
        ) : (
          <div className="py-10 text-center">
            <p className="text-sm font-semibold text-dark">No saved products yet.</p>
            <p className="text-xs text-dark-muted mt-1">
              Add products to <a href="/products" className="text-primary font-bold underline">My Products</a> first, or use the Store tab.
            </p>
          </div>
        )}
      </div>

      {/* Generate button — always visible. Shows live credit cost and
          flips to an upgrade CTA when balance < GENERATE_COST. */}
      <GenerateButton
        busy={busy}
        disabled={!selected}
        onConfirm={() => selected && onConfirm({
          imageUrl: selected.imageUrl,
          name: selected.name,
          description: selected.description,
          source: selected.source,
        })}
      />
    </div>
  );
}

// Per-concept product picker grid — the streamlined replacement for the
// single-product ProductPicker above. Renders one row per concept with a
// product dropdown beside the concept summary. Generate fires
// queue-renders and bounces to /gallery.
//
// Layout: 1 column on mobile, 2 on lg+ so 5 concepts fit in 3 rows of
// (col1, col2, col3) with the 5th in col1 row3 — comfortable density.
function PerConceptPicker({
  concepts,
  savedProducts,
  conceptProducts,
  onChange,
  busy,
  error,
  onGenerate,
}: {
  concepts: AdConcept[];
  savedProducts: UserProduct[];
  conceptProducts: Record<number, string>;
  onChange: (idx: number, productId: string) => void;
  busy: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  if (concepts.length === 0) {
    return <p className="text-sm text-dark-muted">No concepts yet.</p>;
  }
  const total = concepts.length;
  // True when every concept has a product picked.
  const allPicked = concepts.every((_c, i) => !!conceptProducts[i]);
  // Quick action: apply the first concept's picked product to all 5.
  // Cheap UX win for the "same product for everything" case without
  // forcing one-product-fits-all as the default model.
  const applyToAll = () => {
    const firstPick = conceptProducts[0] || savedProducts[0]?.id;
    if (!firstPick) return;
    for (let i = 0; i < total; i++) onChange(i, firstPick);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs text-dark-muted">
        <span>{total} concepts · {savedProducts.length} products in your library</span>
        <button
          type="button"
          onClick={applyToAll}
          disabled={busy}
          className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Use {savedProducts.find((p) => p.id === (conceptProducts[0] || savedProducts[0]?.id))?.name || 'first product'} for all →
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {concepts.map((c, i) => {
          const pickedId = conceptProducts[i] || '';
          const picked = savedProducts.find((p) => p.id === pickedId);
          return (
            <div key={i} className="rounded-xl border border-surface-dark/40 bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-bold">
                  {c.angle || `Concept ${i + 1}`}
                </span>
              </div>
              <p className="font-display font-bold text-dark text-sm leading-snug mb-1">
                {c.headline}
              </p>
              {c.subhead && (
                <p className="text-xs text-dark-muted mb-3 line-clamp-2">{c.subhead}</p>
              )}
              <div className="flex items-center gap-2">
                {picked?.imageUrl ? (
                  <img
                    src={picked.imageUrl}
                    alt={picked.name}
                    className="w-10 h-10 rounded-lg object-cover border border-surface-dark/40 flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-surface-dark/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-bag-3-line text-dark-muted" />
                  </div>
                )}
                <select
                  value={pickedId}
                  onChange={(e) => onChange(i, e.target.value)}
                  disabled={busy}
                  className="flex-1 min-w-0 text-sm rounded-lg border border-surface-dark/50 px-2 py-1.5 bg-white text-dark focus:outline-none focus:border-primary disabled:opacity-50"
                >
                  {savedProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || 'Saved product'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-sm text-red-600 font-medium">{error}</p>
      )}

      <div className="pt-2 border-t border-surface-dark/20">
        <button
          type="button"
          onClick={onGenerate}
          disabled={busy || !allPicked}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Queueing renders…
            </>
          ) : (
            <>
              <i className="ri-sparkling-2-line" />
              Generate {total} ads in My Creatives
            </>
          )}
        </button>
        <p className="text-center text-xs text-dark-muted mt-2">
          1 credit per ad. You'll land on My Creatives to watch them render in parallel.
        </p>
      </div>
    </div>
  );
}

// Live credit-aware Generate CTA. Extracted from the picker so the credit
// math + upgrade flip live in one place.
function GenerateButton({ busy, disabled, onConfirm }: { busy: boolean; disabled: boolean; onConfirm: () => void }) {
  const navigate = useNavigate();
  const { balance, loading: creditsLoading } = useCredits();
  const insufficient = balance !== null && balance < GENERATE_COST;

  return (
    <div className="pt-2 space-y-1.5">
      {insufficient ? (
        <button
          onClick={() => navigate('/checkout')}
          disabled={busy}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow-md shadow-amber-500/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-lock-2-line" /> Upgrade — {GENERATE_COST} credits needed
        </button>
      ) : (
        <button
          onClick={onConfirm}
          disabled={disabled || busy || creditsLoading}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? (
            <><i className="ri-loader-4-line animate-spin" /> Tuning concepts…</>
          ) : (
            <>
              <i className="ri-magic-line" /> Let AI create {GENERATE_COST} ad concepts for this product
              <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">{GENERATE_COST} credits</span>
            </>
          )}
        </button>
      )}
      <p className="text-xs text-dark-muted">
        {insufficient
          ? `You have ${balance ?? 0} credits — need ${GENERATE_COST} to generate.`
          : `Includes 5 AI-generated ads (carousel concepts = 4 frames each). Charged once at concept generation, not per ad.`}
      </p>
    </div>
  );
}

function PickerTab({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: string; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
        active ? 'bg-primary text-white shadow-sm shadow-primary/25' : 'bg-surface text-dark-muted hover:text-dark hover:bg-surface-dark/30'
      }`}
    >
      <i className={icon} /> {label}
    </button>
  );
}

function ProductGrid({
  options,
  selectedKey,
  onSelect,
}: {
  options: PickerOption[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      {options.map((o) => {
        const active = o.key === selectedKey;
        return (
          <button
            key={o.key}
            onClick={() => onSelect(o.key)}
            className={`relative rounded-lg border-2 overflow-hidden text-left p-2 transition-all cursor-pointer bg-white ${active ? 'border-primary shadow-sm shadow-primary/15' : 'border-surface-dark/25 hover:border-primary/40'}`}
          >
            <div className="aspect-square w-full bg-surface rounded-md overflow-hidden mb-1.5">
              {o.imageUrl ? (
                <img src={o.imageUrl} alt={o.name} className="w-full h-full object-cover" loading="lazy" />
              ) : null}
            </div>
            <p className="text-xs font-semibold text-dark line-clamp-2 leading-tight">{o.name}</p>
            {active && (
              <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs">
                <i className="ri-check-line" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-surface-dark/30 bg-white p-6">
      <div className="flex items-center gap-2 mb-3">
        <i className={`${icon} text-primary`} />
        <h2 className="font-display font-bold text-dark text-lg">{title}</h2>
      </div>
      {children}
    </div>
  );
}

// SuggestCompetitorsPanel — collapsible panel above the manual competitor
// rows. Click "Suggest from my brand" → LLM picks 10 likely competitors,
// rendered as a flat text-card grid. Meta-ad validation happens lazily
// after the user clicks Add (POST /api/competitors warms ad_intel_cache),
// so the suggest call returns in ~5-15s instead of ~2min.
function SuggestCompetitorsPanel({
  suggestions,
  loading,
  error,
  fromCache,
  usedProductSignals,
  onSuggest,
  onAdd,
  pickedQueries,
}: {
  suggestions: CompetitorSuggestion[];
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  usedProductSignals: boolean;
  onSuggest: () => void;
  onAdd: (sug: CompetitorSuggestion) => void;
  pickedQueries: string[];
}) {
  const hasSuggestions = suggestions.length > 0;

  return (
    <div className="mb-3 rounded-xl border border-primary/20 bg-primary/[0.03] p-3">
      {!hasSuggestions && !loading ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-dark">
              Don&apos;t know your competitors? Let AI suggest them.
            </p>
            <p className="text-xs text-dark-muted mt-0.5">
              We&apos;ll pick 10 likely competitors from your brand.
            </p>
          </div>
          <button
            type="button"
            onClick={onSuggest}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-sparkling-2-line" /> Suggest
          </button>
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 py-3 text-sm text-dark-muted">
          <i className="ri-loader-4-line animate-spin text-primary text-lg" />
          <span>Finding competitors…</span>
          <span className="text-xs text-dark-muted/70 ml-auto">~5-15s</span>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-xs font-bold text-dark-muted uppercase tracking-wide">
              {suggestions.length} suggestions
              {fromCache && (
                <span className="ml-1.5 text-dark-muted/70 normal-case font-semibold">
                  (cached)
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={onSuggest}
              className="text-xs font-bold text-primary hover:underline cursor-pointer"
              title="Re-fetch suggestions (skips the 14-day cache)"
            >
              Refresh
            </button>
          </div>

          {!usedProductSignals && (
            <div className="mb-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
              <i className="ri-lightbulb-flash-line mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                These picks are based on your brand description alone. Add a few items to{' '}
                <a href="/products" className="font-bold underline hover:no-underline">
                  My Products
                </a>{' '}
                so the AI knows your actual product mix — picks land in the right niche.
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-2">
            {suggestions.map((s) => (
              <SuggestionCard
                key={s.query}
                suggestion={s}
                isAdded={pickedQueries.includes(s.query)}
                onAdd={() => onAdd(s)}
              />
            ))}
          </div>
        </>
      )}

      {error && (
        <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 flex items-start gap-2">
          <i className="ri-error-warning-line mt-0.5" />
          <div className="flex-1">{error}</div>
        </div>
      )}
    </div>
  );
}

function SuggestionCard({
  suggestion,
  isAdded,
  onAdd,
}: {
  suggestion: CompetitorSuggestion;
  isAdded: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="rounded-lg bg-white border border-surface-dark/30 px-3 py-2.5 flex items-start gap-2.5 hover:border-primary/40 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-dark truncate mb-0.5">{suggestion.name}</p>
        <p className="text-xs text-dark-muted line-clamp-2">{suggestion.description}</p>
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={isAdded}
        className={`flex-shrink-0 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
          isAdded
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
            : 'bg-primary hover:bg-primary-hover text-white shadow-sm'
        }`}
        title={isAdded ? 'Already added' : 'Add to analysis'}
      >
        {isAdded ? (
          <><i className="ri-check-line" /> Added</>
        ) : (
          <><i className="ri-add-line" /> Add</>
        )}
      </button>
    </div>
  );
}
